import paper from "paper";
import {
  pieceStitchLines,
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  fullySelectedConstructionGroupIds,
  isVisible,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  tracingImageBounds,
  tracingImageContainsPoint,
  tracingImageOpacity,
  tracingImageRotation,
  type ConstructionLine,
  type ConstraintRef,
  type MatLayout,
  type Project,
  type Piece,
  type Cutout,
  type StitchLine,
  type TracingImage,
  type Vec2,
} from "../model/Project";
import { seamPathData } from "../model/seamGeometry";
import {
  groupRef,
  imageRef,
  lineRef,
  type ObjectRef,
} from "../model/ConstructionObject";
import { renderTracingImageCanvas } from "../tracing/loadTracingImage";
import { type BboxEdge } from "../model/constraintEnforcement";
import { liveRederiveAllAffectedBy } from "../model/connections";
import {
  getCornerGeometry,
  handleDistanceFromCorner,
} from "../model/constructionRounding";
import {
  effectiveGroupPivot,
  effectiveImagePivot,
  effectiveLinePivot,
  getUnrotatedBounds,
  getUnrotatedGroupBounds,
} from "../model/constructionRotation";

/** Snap-candidate ref the Scene needs to render a yellow ring at.
 *  Mirrors the snap-pipeline's `SnapRefLite` union but stays here so
 *  Scene doesn't take a dependency on the enforcement-side type names
 *  for its public-ish setter shape. */
export type SnapCandidateRef =
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  | { kind: "center"; constructionId: string }
  | { kind: "bbox-midpoint"; constructionId: string; edge: BboxEdge }
  | {
      kind: "segment-midpoint";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    };
import type { StampLibrary } from "../stamps/StampLibrary";
import { StampSymbols } from "./StampSymbols";

// All Scene render colors come from CSS custom properties defined in
// `src/styles.css` (per the AGENTS.md "Theming" rule). `cssVar()` reads
// the live computed value, so a future theme override flows through with
// no further changes here.
import { cssVar } from "../colors";
import {
  MatRenderer,
  type MatRenderHost,
  MAT_GRID_MAJOR,
  MAT_PADDING,
} from "./matRenderer";
import {
  ConstructionOverlayRenderer,
  type ConstructionOverlayHost,
  buildAnchorLockMap,
  LEATHER_TRANSFORM_OVERLAY_KINDS,
} from "./constructionOverlayRenderer";
import type { Box } from "../model/constructionResize";
import {
  gatherSnapCandidatePoints,
  type ActiveSnapKinds,
} from "../tools/snapCandidates";
import {
  displayedBoxCenter,
  TRANSFORM_KNOB_OFFSET_MM,
  TRANSFORM_KNOB_RADIUS_MM,
  TRANSFORM_RESIZE_HANDLE_HALF_MM,
} from "../model/transformHandles";
import { pieceDisplayOffset } from "../model/pieceTransform";
import { PieceRenderer, type PieceRenderHost } from "./pieceRenderer";
import { buildNoteGroup } from "./noteRenderer";
import {
  isNoteVisible,
  numberedNotes,
  resolveNoteAnchors,
} from "../model/notes";
import {
  ConstructionHoverController,
  type ConstructionDeleteHoverSpec,
  type ConstructionHoverHost,
} from "./constructionHoverController";
import {
  TransientTransformController,
  type TransientTransformHost,
} from "./transientTransformController";
import { currentPointerKind, hitRadiusFor } from "./pointerPrecision";

const STITCH_STROKE = () => cssVar("--piece-cut");
const SELECTION_STROKE = () => cssVar("--canvas-accent");
/** Selection / ray accent for marks drawn OVER a leather piece. The pale
 *  leather fill washes out the light `--canvas-accent` and a cool blue fights
 *  the warm tan, so leather-context selection highlights and rays use this
 *  near-black charcoal — a neutral high-contrast ink that reads clearly on the
 *  tan (WCAG AAA against the leather fill / edge) (#744 follow-up). */
const LEATHER_SELECTION_STROKE = () => cssVar("--canvas-accent-leather");

/** Snap-candidate dot RADIUS in screen px (#367) — the value is divided by the
 *  live zoom before it reaches paper, so this is what a dot measures on screen
 *  at every zoom (#864's screen-constant rule).
 *
 *  Read it as px, not mm: at 1.6 the dots rendered ~3 px across at 45% alpha
 *  and changed 64 of the canvas's ~600k pixels — drawn, correct, and invisible,
 *  which is a feature that doesn't do its job. The ticket asks for "faint tiny
 *  dots", and tiny they must stay; the reference point is the live snap
 *  indicator that lights up when one actually FIRES, which is radius 2 project
 *  mm plus a 2.2 px ring (~15 px at default zoom, and it grows with zoom).
 *  A 3 px disc leaves that hierarchy intact while being findable. */
const SNAP_CANDIDATE_DOT_RADIUS_PX = 3;
/** Faint on purpose: the dots say "there is a target here", the live snap
 *  indicator says "you are on it". Faint has to stay above the floor of
 *  perceptible, though — against the dark mat, 0.45 put barely 10 levels of
 *  luminance between a dot and the felt. */
const SNAP_CANDIDATE_DOT_ALPHA = 0.7;
/** A hairline in the same ink, so a dot keeps an edge over the pale tan of a
 *  part as well as the dark mat — the two backgrounds it has to read on sit on
 *  opposite sides of the dot's own value. Non-scaling, like every other
 *  affordance stroke. */
const SNAP_CANDIDATE_DOT_STROKE_PX = 1;
const CONSTRUCTION_STROKE = () => cssVar("--construction-stroke");
const CONSTRUCTION_HALO = () => cssVar("--construction-halo");
const CONSTRUCTION_BROKEN = () => cssVar("--construction-broken");

/** A single value ray (#744): a dashed line from `from` to `to` with an
 *  arrowhead and a value label. Provide any of `angleDeg` (degrees, y-up),
 *  `lengthMm` / `radiusMm` (project mm), or an explicit `text`; the label
 *  joins whichever are present. */
/** Root-layer overlays drawn for the selected cutout / seam / part, at its
 *  part's display offset (#1140). The Assemble / Spread slide moves them with
 *  the part. */
const PART_OVERLAY_KINDS = new Set<string>([
  LEATHER_TRANSFORM_OVERLAY_KINDS.overlay,
  "leather-transform-pivot",
  "stitch-edit-path",
  "outline-edit-handles",
]);

export interface GuideRay {
  from: { x: number; y: number };
  to: { x: number; y: number };
  angleDeg?: number;
  lengthMm?: number;
  radiusMm?: number;
  text?: string;
  /** Preset angle step (polyline Angle step #937): angle labels that land on
   *  a multiple of it annotate the math — "144° (72° × 2)" — so step
   *  multiples read apart from complementary values like 108°. */
  angleStepDeg?: number;
  /** Which single angle marker to render (#938): "corner" hides the
   *  horizontal marker, "horizontal" hides the corner marker. Absent =
   *  both (the pre-existing dual display, still used by the edit rays). */
  angleRef?: "horizontal" | "corner";
  /** Previous vertex of a polyline / polygon (#747): when set, an extra arc is
   *  drawn at `from` showing the interior angle between the incoming edge
   *  (`from`→`cornerPrev`) and this segment (`from`→`to`). */
  cornerPrev?: { x: number; y: number };
  /** When true, also draw a dashed "ghost" ray from `from` to `cornerPrev` — the
   *  OLD segment position — so the interior arc has a visible line on both
   *  sides. Used for a point move (#746), where the old segment isn't otherwise
   *  drawn; left off for polyline edges, whose previous segment is already on
   *  the canvas. */
  cornerPrevGhost?: boolean;
  /** Override the over-leather accent decision (#972). The default tests the
   *  ray's ORIGIN, which is right for a ray that springs from somewhere and
   *  heads elsewhere — but an offset ray's origin is the perpendicular foot,
   *  sitting exactly ON the source edge, where "is this leather?" is genuinely
   *  ambiguous. Such callers test a point that represents where the ray really
   *  lies (its midpoint) and pass the answer here. Mirrors the same escape
   *  hatch `setDragAxisGuide` already takes. */
  overLeather?: boolean;
}

/** Whether the user has asked the OS to minimise motion — gates the
 *  Spread/Assembled slide (#652) so it snaps instead. */
function prefersReducedMotion(): boolean {
  try {
    return (
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

/** Selection target for editing a stitching-defaults block.
 *  - `project`: the project-level defaults block.
 *  - `piece`:   a part's override block.
 *  - `bucket`:  a sub-group of overriding stitch lines (per part) that
 *               share the same override settings. Identified by line IDs so
 *               the selection survives edits that change the bucket's
 *               serialized key. */
export type StitchingScope =
  | { kind: "project" }
  | { kind: "piece"; pieceId: string }
  | { kind: "bucket"; pieceId: string; lineIds: string[] };

/** Which outline the leather Edit-points tool is editing (#969/#970). */
export type OutlineEditHandlesSpec =
  | { kind: "piece"; pieceId: string }
  | { kind: "cutout"; pieceId: string; cutoutId: string }
  | { kind: "stitch"; pieceId: string; stitchId: string };

export interface SceneSelection {
  /** Selected notes (#157). Their own set rather than a slot on the piece
   *  selection: a note can point AT a part without belonging to it, and
   *  selecting the note must not select the part it references. */
  noteIds?: string[];
  /** Lead piece in the multi-select. Drives the Properties panel and
   *  the legacy "single piece selected" code paths that haven't moved
   *  to `pieceIds[]` yet. Same lead/full-set split that
   *  `constructionId` / `constructionIds` uses. When `pieceIds` is
   *  also supplied, the scene's normalizer derives the lead from the
   *  tail of `pieceIds` and ignores this field. */
  pieceId: string | null;
  /** Full multi-select set of part ids. Normalized by
   *  `Scene.setSelection` so callers can pass either `pieceId`,
   *  `pieceIds`, or both — the stored state ends up coherent with
   *  `pieceId = pieceIds[last]`. Empty when no piece is selected.
   *  Multi-piece selections (> 1 entry) force every sub-item id below
   *  (`decorationId` / `holeId` / `cutoutId` / `stitchId`) to null —
   *  the two scopes are mutually exclusive. */
  pieceIds?: string[];
  decorationId: string | null;
  /** Hole within the selected piece. Mutually exclusive with decorationId /
   *  cutoutId / stitchId. Older callers pass undefined; the scene normalizes
   *  to null. */
  holeId?: string | null;
  /** Cutout within the selected piece. Mutually exclusive with decorationId /
   *  holeId / stitchId. */
  cutoutId?: string | null;
  /** Stitch line within the selected piece. When multiple stitch lines are
   *  selected (shift / cmd-click to build a "Match selected" pair, #199), this
   *  points at the **lead** — last-added line — which drives the Properties
   *  panel. The full set lives in `stitchIds`. Mutually exclusive with the
   *  others. */
  stitchId?: string | null;
  /** The full multi-select set of stitch line ids. Stitch ids are globally
   *  unique, so the set can span two pieces (a match may link seams on
   *  different parts). Normalized by `Scene.setSelection` so a caller can pass
   *  `stitchId`, `stitchIds`, or both and end up coherent with
   *  `stitchId = stitchIds[last]`. Empty when no stitch is selected. */
  stitchIds?: string[];
  /** Construction (guide) line within the selected piece. When
   *  multiple construction lines are selected (multi-select via
   *  shift/cmd-click in the layers panel, lasso, etc.), this points at
   *  the **lead** — the most-recently-added line, which the properties
   *  panel uses as its focus. The full set lives in `constructionIds`.
   *  Mutually exclusive with decorationId / holeId / cutoutId /
   *  stitchId. */
  constructionId?: string | null;
  /** The full multi-select set of construction line ids. Normalized by
   *  `Scene.setSelection` so that whichever a caller passes — just
   *  `constructionId`, just `constructionIds`, or both — ends up with
   *  a consistent state: `constructionId = constructionIds[last]`,
   *  and the size-1 single-select shorthand still works for every
   *  pre-multi-select call site. Empty when no construction line is
   *  selected. */
  constructionIds?: string[];
  /** Selected construction groups, treated as entities. Distinct from
   *  `constructionIds` so the layers panel can tell "the user clicked
   *  the group header" apart from "the user multi-selected exactly
   *  the group's members" — same set of line highlights, different
   *  intent. Cmd+G with this set populated wraps those groups
   *  (alongside any selected loose lines) in a new parent group.
   *  Cleared whenever the construction-line selection changes via
   *  any path that doesn't explicitly preserve it. */
  constructionGroupIds?: string[];
  /** When the user picks "Stitching" from the Layers panel — at the
   *  project root or under a part — this points the Properties panel at the
   *  matching defaults block. Mutually exclusive with the per-item ids
   *  above. */
  stitchingScope?: StitchingScope | null;
  /** Tracing image selected from the Layers panel (issue #498) or the
   *  canvas (issue #509). The **lead** image — last-added entry of
   *  `tracingImageIds` — which drives the Properties panel. A
   *  project-scoped reference photo, not tied to any piece. Cleared by
   *  any selection that doesn't carry it forward. */
  tracingImageId?: string | null;
  /** The full multi-select set of tracing-image ids (issue #509).
   *  Normalized by `Scene.setSelection` the same way `constructionIds`
   *  is: dedup'd, with `tracingImageId = tracingImageIds[last]`.
   *  Images can be selected alongside construction lines / groups and
   *  dragged or grouped together. Empty when no image is selected. */
  tracingImageIds?: string[];
}

export interface SceneCallbacks {
  onPiecePicked?: (pieceId: string) => void;
  onDecorationPicked?: (pieceId: string, decorationId: string) => void;
}

/** Structured return shape from `Scene.hitTest` — every field is
 *  optional so callers can shape-check whichever kind they care
 *  about. */
export interface HitTestResult {
  pieceId?: string;
  decorationId?: string;
  holeId?: string;
  cutoutId?: string;
  stitchId?: string;
  constructionId?: string;
  /** When the cursor is over a selected construction group's bbox
   *  frame, this carries the owning group's id so the Construction
   *  tool's select sub-mode can start a whole-group drag without
   *  the user having to click a specific member line. */
  constructionGroupId?: string;
  /** When the cursor is over an editable construction-line handle,
   *  these locate the exact anchor point inside the line's parsed
   *  pathData. */
  constructionHandle?: { pathIndex: number; segmentIndex: number };
  /** When the cursor is over a bezier tangent-handle endpoint
   *  (the hollow yellow circle at the tip of a tangent line),
   *  these locate which side of which segment to mutate. */
  bezierHandle?: {
    pathIndex: number;
    segmentIndex: number;
    side: "in" | "out";
  };
  /** When the cursor is over the top-of-bbox rotation knob hanging
   *  above a selected construction line, this carries the owning
   *  line's id so ConstructionTool's "select" sub-mode can start a
   *  rotation drag instead of a move. */
  lineRotationHandleId?: string;
  /** Group analog of `lineRotationHandleId`. */
  groupRotationHandleId?: string;
  /** When the cursor is over a tracing image's placed rectangle in
   *  the Construction tool's Select sub-mode (issue #509), this
   *  carries the image's id so the tool can select / drag it. */
  tracingImageId?: string;
}

/** Axis-aligned bounding box in project mm. */

/**
 * Renders a Project to Paper.js layers. Re-renders fully on each model change
 * (the project is small enough that this is cheap and keeps logic simple).
 */
export class Scene
  implements
    MatRenderHost,
    ConstructionOverlayHost,
    ConstructionHoverHost,
    TransientTransformHost
{
  private scope: paper.PaperScope;
  private library: StampLibrary;
  private symbols: StampSymbols;
  private rootLayer: paper.Layer;
  /** Cutting-mat background renderer (#852): owns the mat + brand render
   *  cluster (grid, ticks, brand block) plus the mat-layout computation and
   *  its caches. Scene drives it from `render()` and delegates the public
   *  mat queries to it via the `MatRenderHost` interface. */
  private matRenderer = new MatRenderer(this);
  /** Construction-overlay visual builders (#854): owns the pure-ish item
   *  CONSTRUCTORS (the `build*` / `render*` methods that turn a construction
   *  line / guide ray / group into paper items). Scene's `render()` and the
   *  transient `refresh*` lifecycle stay here and call these builders through
   *  the `ConstructionOverlayHost` interface. */
  private constructionOverlayRenderer = new ConstructionOverlayRenderer(this);
  /** Leather-piece visual builders (#856): owns `renderPiece` and its
   *  sub-renderers (outline / holes / cutouts, decorations / stamps / fills /
   *  offsets, stitch lines, hardware). Scene's `render()` orchestration and the
   *  selection / handle-visibility gating that decides WHEN to call
   *  `renderPiece` stay here and drive the renderer through the
   *  `PieceRenderHost` interface. */
  private pieceRenderer = new PieceRenderer(this);
  /** Transient construction hover / crosshair overlay cycle (#858): owns the
   *  segment / delete / panel / constraint hover overlays, the snap-candidate +
   *  draft-snap-point rings, the coincident-anchor crosshairs, and their shared
   *  per-frame marching-ants loop. Scene keeps thin delegators for the public
   *  setters (the tools / app / panels call Scene) and drives the `refresh*`
   *  lifecycle from `render()` through the `ConstructionHoverHost` interface. */
  private constructionHover = new ConstructionHoverController(this);
  /** Live drag-preview transform machinery (#860): owns the transient
   *  per-line + per-group rotation overrides, the snap-partner translation
   *  bookkeeping, the bidirectional mirror overlay rotation, the live
   *  mirror / offset re-derive that keeps derived children tracking their
   *  source, and the pivot crosshair lifecycle. Scene keeps thin delegators
   *  for the public methods (the tools / app call Scene) and reaches the
   *  shared scope / layers / project / rendering helpers back through the
   *  `TransientTransformHost` interface. */
  private transientTransform = new TransientTransformController(this);
  /** Incremental-render cache for piece groups (#579). An *unselected* piece's
   *  rendered group is a pure function of its model data plus a handful of
   *  global flags (edit mode, handle toggles, theme colors) captured in
   *  `pieceGlobalSig`; when both match, the previously-built group is reused
   *  instead of rebuilt, so editing one piece in a large project no longer
   *  rebuilds them all. Selected pieces are never cached (their styling depends
   *  on live selection / hover and they're the ones tools transiently drag), so
   *  a stale reused group is impossible. `getPieceGroup` also evicts, as a
   *  belt-and-suspenders guard for any transient transform.
   *
   *  Keyed by piece id and matched by *object identity*: the project model is
   *  immutable (commits map pieces, returning the same object for unchanged
   *  ones — undo/redo depends on this), so an unchanged piece keeps its
   *  reference and any edit produces a new one. Identity is therefore both
   *  correct and O(1) — no per-render hashing. */
  private pieceGroupCache = new Map<
    string,
    { piece: Piece; group: paper.Item }
  >();
  /** Signature of the global render state the cached piece groups were built
   *  under; when it changes, the whole piece cache is dropped. */
  private pieceGlobalSig = "";
  /** Warped + cropped rasters for the project's tracing images (issue
   *  #489), keyed by image id. Deriving one needs an async fetch + a
   *  per-pixel warp, so render() places cached canvases synchronously
   *  and kicks off a background derivation (then re-renders) on miss.
   *  "error" entries stop a broken URL from re-fetching every render. */
  //  Keyed by a CONTENT signature (the source + quad + size that
  //  actually determine the bitmap), NOT the image id, so duplicate
  //  images that share the same resource decode / warp ONCE and reuse
  //  the same canvas instead of each fetching + warping the identical
  //  source. When an image's content changes — e.g. the 200-DPI final
  //  copy lands after placement (#500) — its signature changes, so the
  //  new bitmap loads under the new key and the old one is evicted once
  //  unreferenced.
  private tracingImageCanvasByContent = new Map<
    string,
    HTMLCanvasElement | "loading" | "error"
  >();
  /** Transient cropped previews handed over by the trace dialog
   *  (issue #510), keyed by image id. Shown at the target bounds while
   *  the full-resolution warp derives, then dropped once the real
   *  raster lands. */
  private tracingImagePreview = new Map<string, HTMLCanvasElement>();
  /** Last live rotation (degrees) pushed via `setTracingImageRotationLive`
   *  per image (issue #526), so the live setter can rotate the rendered
   *  raster + overlay by the *delta* since the previous tick. Delta-based
   *  `.rotate()` keeps the raster (matrix) and the dashed overlay (a
   *  Path, which bakes rotation into its points) perfectly in sync —
   *  the absolute `.rotation` setter desynced them. Cleared whenever the
   *  image is re-rendered at its committed angle. */
  private tracingImageLiveRotation = new Map<string, number>();
  private project: Project | null = null;
  private selection: SceneSelection = {
    pieceId: null,
    pieceIds: [],
    decorationId: null,
    holeId: null,
    cutoutId: null,
    stitchId: null,
    constructionId: null,
    constructionIds: [],
    constructionGroupIds: [],
    stitchingScope: null,
    tracingImageId: null,
    tracingImageIds: [],
  };
  private callbacks: SceneCallbacks = {};
  /** Tri-state edit mode that controls how the leather + sub-features are
   *  rendered:
   *  - "none":       display mode. Leather minus holes/cutouts; no markers
   *                  for any sub-feature; no stitch hairline guide.
   *  - "structural": Hole / Cutout tools. Full leather + editable hole and
   *                  cutout markers on top.
   *  - "stitch":     Stitch tool. Same leather as display mode (so the user
   *                  sees the finished cut) plus the stitch hairline guide;
   *                  hole / cutout markers are hidden so the seam is the
   *                  focus. */
  private editMode: "none" | "structural" | "stitch" = "none";
  /** When true, every construction line gets a small dot rendered at
   *  each anchor point so the user can grab and drag it to reshape the
   *  line. Toggled on by the Construction tool while it's active. */
  private showConstructionHandles = false;
  /** True while the Construction tool is active, in *any* sub-mode (#649).
   *  Unlike `showConstructionHandles` (which is edit-sub-mode only), this
   *  flags the whole construction-editing context so leather reads as the
   *  scaffold's companion: pieces render as unfilled outlines and stitch
   *  lines show their seam guide. */
  private constructionActive = false;
  /** Cut-layout view (#652). In "spread" each linked part is drawn at its
   *  `constructionOutline.layout` offset (laid out for cutting); in "assembled"
   *  the offset is ignored so linked parts sit at their construction positions.
   *  Unlinked parts are unaffected either way. */
  private partLayoutView: "spread" | "assembled" = "spread";
  /** RAF handle for the in-flight Spread/Assembled slide, or null (#652). */
  private partLayoutAnimRaf: number | null = null;
  /** When true, every round-eligible sharp corner of a construction
   *  line gets a small inside-the-corner handle so the user can drag
   *  it to inscribe a circular arc (issue #100). Toggled on by the
   *  Construction tool's Round sub-mode. */
  private showCornerHandles = false;
  /** Preset radius (mm) the Construction tool's Round sub-mode is
   *  currently locked to, mirrored from the properties-panel input.
   *  Drives the visual position of corner handles for un-rounded
   *  corners — picking a 10 mm preset chip should move the handle
   *  outward to the arc-centre for 10 mm so the user can see what a
   *  click would commit. `null` falls back to the small default
   *  preview radius (`DEFAULT_CORNER_PREVIEW_RADIUS_MM`). */
  private roundPresetRadius: number | null = null;
  /** Effective "round all corners" mode (issue #100 follow-up).
   *  Mirrors `settings.roundAllCorners XOR ctrl/cmd-held` from
   *  the snap toolbar's round-mode toggle. When true, every
   *  round-eligible corner handle on a line renders filled to
   *  signal that a drag will round all of them; when false only
   *  corners that already carry a rounding entry render filled. */
  private roundModeAll = true;
  /** Transient override for corner-handle positions, set by the
   *  Construction tool per-frame during a Round drag so the handle
   *  dots follow the cursor in lock-step with the previewed arc.
   *  When `applyToAll` is true, every round-eligible corner on the
   *  same line uses the override radius (their dots move together
   *  along their OWN bisectors — that's what "linked" looks like
   *  during a drag). When false, only the corner addressed by
   *  `(pathIndex, segmentIndex)` moves. Cleared on drag end /
   *  cancel; the next render picks up the committed radius from
   *  the project metadata. */
  private transientCornerHandle: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    radius: number;
    applyToAll: boolean;
  } | null = null;
  /** Transient pathData overrides keyed by construction-line id,
   *  set during a Round drag (issue #100) so offset lines that
   *  follow the dragged source can visibly track the in-flight
   *  rounded outline. Cleared on drag end / cancel. */
  private transientOffsetPaths: Map<string, string> | null = null;
  /** When true and a single construction line is selected, render
   *  the rotation overlay (tilted dashed bbox + top handle + optional
   *  hover-driven corner arcs) so the user can rotate it. Toggled on
   *  by ConstructionTool's "select" sub-mode (issue #67). */
  private showConstructionRotationHandles = false;
  /** Which corner of the currently selected construction line the
   *  cursor is hovering just outside — drives the corner rotation-arc
   *  visual. Cleared when the cursor leaves any corner's hover zone. */
  private lineRotationHoverCorner: {
    constructionId: string;
    corner: "tl" | "tr" | "bl" | "br";
  } | null = null;
  /** Group bbox corner under hover for rotation — the group analog of
   *  `lineRotationHoverCorner`, used to render the corner arc inside
   *  the group overlay. */
  private groupRotationHoverCorner: {
    constructionGroupId: string;
    corner: "tl" | "tr" | "bl" | "br";
  } | null = null;
  /** Live-rotation subscribers — fired from `setTransientLineRotation`
   *  with the effective absolute angle (committed `rotation` +
   *  in-flight delta, normalised to (-180, 180]). The PropertiesPanel
   *  registers one of these so its scrubber + number field track
   *  the canvas drag without waiting for a commit. */
  private rotationLiveListeners = new Set<
    (spec: { lineId: string; deg: number } | null) => void
  >();
  private matVisible = false;
  private matUnits: "mm" | "in" = "mm";
  /** The in-draw snap indicator for the leather Draw tools (#761) — a
   *  light-blue ring shown at the vertex / grid point the cursor has snapped
   *  to while drawing a part / cut-out / hole / stitch. Independent of the
   *  construction snap overlays above (those are yellow and constraint-aware). */
  private drawSnapPoint: { x: number; y: number } | null = null;
  private drawSnapPointItem: paper.Item | null = null;

  /** Which snap tiers to speckle with faint dots (#367). Driven by `app.ts`,
   *  which combines the user's preference with the live snap toggles — the
   *  Scene doesn't see tool settings. Per-tier rather than a single flag so the
   *  dots can follow the Points and Line chips independently, which is the part
   *  of the ticket about knowing which toggle catches which points. */
  private snapCandidateKinds: ActiveSnapKinds = { point: false, line: false };
  /** Gathering snap targets parses every path in the project, which is far too
   *  much to redo on each frame of a zoom (zoom triggers a full re-render so
   *  screen-constant sizes stay honest). Cached on the project OBJECT identity
   *  — a zoom render passes the same immutable project, an edit passes a new
   *  one — plus the layout view, since the points are gathered in displayed
   *  space (#786), plus the armed tiers, since those decide which kinds of
   *  point are in the list (crossings in particular are the expensive part). */
  private snapCandidateCache: {
    project: Project;
    view: "spread" | "assembled";
    kinds: ActiveSnapKinds;
    points: { x: number; y: number }[];
  } | null = null;
  /** The in-flight project during a drag that hasn't committed (#367 follow-up)
   *  — see `candidateSourceProject`. Null whenever nothing is being dragged. */
  private snapCandidateLiveProject: Project | null = null;
  /** The drawn dot group, kept so a mid-drag update can replace just it instead
   *  of re-rendering the scene under the tool that's mutating its own items. */
  private snapCandidateDotsItem: paper.Item | null = null;
  /** Angle-snap drag guide (#725): a dashed inference line drawn while a
   *  selection drag is constrained to a common angle. */
  private dragAxisGuideItem: paper.Item | null = null;
  /** Guide rays (#744): on-canvas value rays (angle / length / distance /
   *  radius) drawn while drawing or editing. Owned + replaced each call. */
  private guideRaysItem: paper.Item | null = null;
  /** When false, `setGuideRays` renders nothing (the show/hide rays toggle,
   *  #745). Defaults true; the app drives it per mode. */
  private raysEnabled = true;

  constructor(scope: paper.PaperScope, library: StampLibrary) {
    this.scope = scope;
    this.library = library;
    this.symbols = new StampSymbols(scope, library);
    scope.activate();
    this.rootLayer = scope.project.activeLayer ?? new scope.Layer();
    this.rootLayer.name = "scene";
  }

  setCallbacks(c: SceneCallbacks) {
    this.callbacks = c;
  }

  setSelection(
    sel: SceneSelection,
    opts: { autoPromoteGroups?: boolean } = {},
  ) {
    // Normalize the construction-line selection so callers can pass
    // either the legacy single-select `constructionId` or the new
    // `constructionIds[]` set (or both) and the stored state is
    // always coherent: `constructionIds` holds the whole set, dedup'd
    // and with empty/null entries filtered out, while
    // `constructionId` is the "lead" — last entry of the set — that
    // drives the properties panel and the selected-style render.
    const rawIds =
      sel.constructionIds ?? (sel.constructionId ? [sel.constructionId] : []);
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const id of rawIds) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    // Normalize `constructionGroupIds` with the same dedup pass so
    // callers can pass a single id (or duplicates from a re-entered
    // shift-click flow) and the stored state stays clean.
    const rawGroupIds = sel.constructionGroupIds ?? [];
    const groupSeen = new Set<string>();
    const groupIds: string[] = [];
    for (const id of rawGroupIds) {
      if (!id || groupSeen.has(id)) continue;
      groupSeen.add(id);
      groupIds.push(id);
    }
    // Auto-promotion (on by default): any existing construction
    // group whose *entire* descendant line set is in `ids` joins
    // `groupIds`. Reads to the user as "select all of a group's
    // members → the group is also selected" — surfaces the bbox
    // overlay, switches the Properties panel to the group view,
    // and lets a follow-up Cmd+G wrap the existing group as a
    // subgroup rather than stripping its members. Opt-out is the
    // drill-to-leaf path, where the user explicitly wants to act
    // on a single member without the enclosing group following.
    // Normalize the tracing-image selection (issue #509) with the
    // same dedup pass so callers can pass `tracingImageId` (single),
    // `tracingImageIds` (set), or both, and the stored state stays
    // coherent with `tracingImageId = tracingImageIds[last]`.
    const rawImageIds =
      sel.tracingImageIds ?? (sel.tracingImageId ? [sel.tracingImageId] : []);
    const imageSeen = new Set<string>();
    const imageIds: string[] = [];
    for (const id of rawImageIds) {
      if (!id || imageSeen.has(id)) continue;
      imageSeen.add(id);
      imageIds.push(id);
    }
    const autoPromote = opts.autoPromoteGroups !== false;
    if (
      autoPromote &&
      this.project &&
      (ids.length > 0 || imageIds.length > 0)
    ) {
      for (const gid of fullySelectedConstructionGroupIds(
        this.project,
        ids,
        imageIds,
      )) {
        if (!groupSeen.has(gid)) {
          groupSeen.add(gid);
          groupIds.push(gid);
        }
      }
    }
    // A selected construction group owns its descendant images just as
    // it owns its descendant lines (issue #539) — selecting the group
    // (layers-panel header, canvas drill, Cmd+G, etc.) must light up
    // and carry its images too, not just its lines. Fold every selected
    // group's descendant images into the image set so every
    // group-selection path stays consistent without each caller having
    // to enumerate them.
    if (this.project) {
      for (const gid of groupIds) {
        for (const iid of descendantConstructionImageIds(this.project, gid)) {
          if (!imageSeen.has(iid)) {
            imageSeen.add(iid);
            imageIds.push(iid);
          }
        }
      }
    }
    // Normalize the part selection (issue #251 multi-select): callers
    // can pass either the legacy single-select `pieceId` or the new
    // `pieceIds[]` (or both); the stored state always carries the
    // dedup'd full set and a lead = last entry. A multi-piece
    // selection (>1 entries) forces every sub-item id to null — the
    // "many parts" and "one specific sub-item" scopes are mutually
    // exclusive, and propagating a stale sub-item from the previous
    // single selection would mislead the Properties panel.
    const rawPieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
    const pieceSeen = new Set<string>();
    const pieceIds: string[] = [];
    for (const id of rawPieceIds) {
      if (!id || pieceSeen.has(id)) continue;
      pieceSeen.add(id);
      pieceIds.push(id);
    }
    // Normalize the stitch-line selection (#199 path B): callers pass a single
    // `stitchId` or the `stitchIds[]` set (or both). The set is dedup'd and the
    // lead is its last entry. Stitch ids are globally unique so the set can span
    // pieces — the lead's `pieceId` (set by the caller) drives the panel.
    const rawStitchIds = sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
    const stitchSeen = new Set<string>();
    const stitchIds: string[] = [];
    for (const id of rawStitchIds) {
      if (!id || stitchSeen.has(id)) continue;
      stitchSeen.add(id);
      stitchIds.push(id);
    }
    const leadStitchId = stitchIds[stitchIds.length - 1] ?? null;
    const leadPieceId = pieceIds[pieceIds.length - 1] ?? null;
    const multiPiece = pieceIds.length > 1;
    this.selection = {
      pieceId: leadPieceId,
      pieceIds,
      decorationId: multiPiece ? null : sel.decorationId,
      holeId: multiPiece ? null : (sel.holeId ?? null),
      cutoutId: multiPiece ? null : (sel.cutoutId ?? null),
      stitchId: multiPiece ? null : leadStitchId,
      stitchIds: multiPiece ? [] : stitchIds,
      constructionId: ids[ids.length - 1] ?? null,
      constructionIds: ids,
      constructionGroupIds: groupIds,
      stitchingScope: sel.stitchingScope ?? null,
      tracingImageId: imageIds[imageIds.length - 1] ?? null,
      tracingImageIds: imageIds,
      // Notes (#157). This object is rebuilt field by field rather than spread
      // from `sel`, so anything not named here is silently dropped — which is
      // what happened to noteIds until it was listed.
      noteIds: sel.noteIds ?? [],
    };
    if (this.project) this.render(this.project);
  }

  setEditMode(mode: "none" | "structural" | "stitch") {
    if (this.editMode === mode) return;
    this.editMode = mode;
    if (this.project) this.render(this.project);
  }

  setShowConstructionHandles(visible: boolean) {
    if (this.showConstructionHandles === visible) return;
    this.showConstructionHandles = visible;
    if (this.project) this.render(this.project);
  }

  /** Enter / leave the construction-editing display (#649): pieces drop
   *  their fill and render as solid outlines, and stitch seam guides show.
   *  Driven by the Construction tool's activate / deactivate. */
  setConstructionActive(active: boolean) {
    if (this.constructionActive === active) return;
    this.constructionActive = active;
    if (this.project) this.render(this.project);
  }

  /** Switch the cut-layout view (#652). "spread" applies each linked part's
   *  `layout` offset; "assembled" ignores it (construction positions). The
   *  linked parts slide between the two positions rather than snapping. */
  setPartLayoutView(view: "spread" | "assembled") {
    if (this.partLayoutView === view) return;
    const prev = this.partLayoutView;
    this.partLayoutView = view;
    if (!this.project) return;
    if (!this.animatePartLayoutTransition(prev, view)) {
      this.render(this.project);
    }
  }

  /** Slide linked parts between their construction (Assembled) and laid-out
   *  (Spread) positions when the view toggles, by tweening the existing piece
   *  groups' transform — no per-frame re-render. The mat is already sized for
   *  the Spread envelope in both views (see `computeMatLayout`), so it stays put
   *  while parts move. Returns false (caller renders instantly) when there's
   *  nothing to animate or the user prefers reduced motion. */
  private animatePartLayoutTransition(
    prev: "spread" | "assembled",
    next: "spread" | "assembled",
  ): boolean {
    if (!this.project) return false;
    if (typeof requestAnimationFrame !== "function") return false;
    if (prefersReducedMotion()) return false;
    const from = prev === "spread" ? 1 : 0;
    const to = next === "spread" ? 1 : 0;
    const movers: { group: paper.Item; dx: number; dy: number }[] = [];
    for (const child of this.rootLayer.children) {
      if (child.data?.kind !== "piece") continue;
      const piece = this.project.pieces.find(
        (p) => p.id === child.data.pieceId,
      );
      const layout = piece?.constructionOutline?.layout;
      if (layout && (layout.dx !== 0 || layout.dy !== 0)) {
        movers.push({ group: child, dx: layout.dx, dy: layout.dy });
      }
    }
    if (movers.length === 0) return false; // nothing offset → just render
    // The selected object's overlays — its transform frame + pivot crosshair,
    // its Edit-points outline and handles — sit on the root layer, not inside
    // the part's group, so they slide alongside it (#1140). Every one of them
    // belongs to the selection's part.
    const owner = movers.find(
      (m) => m.group.data.pieceId === this.selection.pieceId,
    );
    if (owner) {
      for (const child of this.rootLayer.children) {
        if (PART_OVERLAY_KINDS.has(child.data?.kind)) {
          movers.push({ group: child, dx: owner.dx, dy: owner.dy });
        }
      }
    }

    this.cancelPartLayoutAnim();
    const duration = 220;
    const start = performance.now();
    let last = from;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      // easeInOutQuad
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      const f = from + (to - from) * eased;
      const df = f - last;
      if (df !== 0) {
        for (const m of movers) {
          m.group.translate(new this.scope.Point(m.dx * df, m.dy * df));
        }
        last = f;
        this.scope.view.update();
      }
      this.partLayoutAnimRaf = p < 1 ? requestAnimationFrame(tick) : null;
    };
    this.partLayoutAnimRaf = requestAnimationFrame(tick);
    return true;
  }

  private cancelPartLayoutAnim() {
    if (this.partLayoutAnimRaf !== null) {
      cancelAnimationFrame(this.partLayoutAnimRaf);
      this.partLayoutAnimRaf = null;
    }
  }

  getPartLayoutView(): "spread" | "assembled" {
    return this.partLayoutView;
  }

  /** PieceRenderHost: the piece builders read the current structural /
   *  stitch edit mode and the construction-editing flag — both stay
   *  single-definition on Scene because tools + other renderers read them
   *  too. */
  getEditMode(): "none" | "structural" | "stitch" {
    return this.editMode;
  }

  isConstructionActive(): boolean {
    return this.constructionActive;
  }

  setShowCornerHandles(visible: boolean) {
    if (this.showCornerHandles === visible) return;
    this.showCornerHandles = visible;
    if (this.project) this.render(this.project);
  }

  /** Toggle the per-line rotation overlay (tilted dashed bbox + top
   *  handle). Driven by ConstructionTool's "select" sub-mode so the
   *  handles only appear while the move sub-tool is active. */
  setShowConstructionRotationHandles(visible: boolean) {
    if (this.showConstructionRotationHandles === visible) return;
    this.showConstructionRotationHandles = visible;
    this.lineRotationHoverCorner = null;
    this.groupRotationHoverCorner = null;
    if (this.project) this.render(this.project);
  }

  /** Set or clear which corner of the selected construction line is
   *  being hovered for rotation. Drives the quarter-circle arc visual
   *  that appears just outside the corner. Re-renders only when the
   *  value changes. */
  setLineRotationHoverCorner(
    spec: { constructionId: string; corner: "tl" | "tr" | "bl" | "br" } | null,
  ): void {
    const same =
      (spec === null && this.lineRotationHoverCorner === null) ||
      (spec !== null &&
        this.lineRotationHoverCorner !== null &&
        spec.constructionId === this.lineRotationHoverCorner.constructionId &&
        spec.corner === this.lineRotationHoverCorner.corner);
    if (same) return;
    this.lineRotationHoverCorner = spec;
    if (this.project) this.render(this.project);
  }

  /** Set or clear which corner of the selected construction GROUP
   *  is being hovered for rotation. Drives the quarter-arc shown
   *  inside the group's bbox overlay. */
  setGroupRotationHoverCorner(
    spec: {
      constructionGroupId: string;
      corner: "tl" | "tr" | "bl" | "br";
    } | null,
  ): void {
    const same =
      (spec === null && this.groupRotationHoverCorner === null) ||
      (spec !== null &&
        this.groupRotationHoverCorner !== null &&
        spec.constructionGroupId ===
          this.groupRotationHoverCorner.constructionGroupId &&
        spec.corner === this.groupRotationHoverCorner.corner);
    if (same) return;
    this.groupRotationHoverCorner = spec;
    if (this.project) this.render(this.project);
  }

  /** Push a transient rotation override for an in-progress drag.
   *  Applies the delta (newDeg - lastAppliedDeg) to every rendered
   *  item that belongs to the dragged construction line — yellow
   *  path, halo, hairline, anchor handles, corner handles, AND the
   *  rotation-overlay (so the bbox tilts with the line). Pass `null`
   *  to clear; the next render picks up the committed state. */
  setTransientLineRotation(
    spec: {
      constructionId: string;
      cx: number;
      cy: number;
      deg: number;
      bidirectionalMirror?: {
        mirrorId: string;
        derivedPivot: { x: number; y: number };
        sign: 1 | -1;
      };
    } | null,
  ): void {
    this.transientTransform.setTransientLineRotation(spec);
  }

  /** TransientTransformHost: fire the live-rotation subscribers. The
   *  subscriber set + `subscribeRotationLive` stay on Scene (the
   *  PropertiesPanel subscribes through Scene); the transient-transform
   *  controller computes the effective absolute angle and asks Scene to
   *  notify. */
  notifyRotationLive(spec: { lineId: string; deg: number } | null): void {
    for (const fn of this.rotationLiveListeners) fn(spec);
  }

  /** Group-level mirror of `setTransientLineRotation`. Applies the
   *  delta `(newDeg - lastAppliedDeg)` to every rendered item that
   *  belongs to one construction group (members + bbox overlay)
   *  about `(cx, cy)` so the visual tumbles with the cursor during a
   *  group rotation drag. Pivot crosshair lifecycle mirrors the
   *  per-line setter. */
  setTransientGroupRotation(
    spec: {
      constructionGroupId: string;
      cx: number;
      cy: number;
      deg: number;
    } | null,
  ): void {
    this.transientTransform.setTransientGroupRotation(spec);
  }

  /** Subscribe to live rotation updates fired while the user drags a
   *  construction line's rotation handle. Returns an unsubscribe
   *  function the caller is expected to invoke before its listener
   *  closure goes out of scope (e.g., when the PropertiesPanel
   *  re-renders and replaces its DOM nodes). The listener fires
   *  with the effective absolute angle on every transient change
   *  and with `null` when the drag ends / cancels. */
  subscribeRotationLive(
    fn: (spec: { lineId: string; deg: number } | null) => void,
  ): () => void {
    this.rotationLiveListeners.add(fn);
    return () => {
      this.rotationLiveListeners.delete(fn);
    };
  }

  /** Update the previewed corner-rounding radius for un-rounded
   *  corners (issue #100). Re-renders so handles move to the
   *  arc-centre for the new value. No-op when the value hasn't
   *  changed; no-op rendering when corner handles aren't currently
   *  visible (the value still updates so the next time handles
   *  appear they pick up the latest preset). */
  setRoundPresetRadius(mm: number | null) {
    if (this.roundPresetRadius === mm) return;
    this.roundPresetRadius = mm;
    if (this.showCornerHandles && this.project) this.render(this.project);
  }

  /** Mirror the snap-toolbar's effective "round all corners" mode
   *  (issue #100 follow-up): `settings.roundAllCorners XOR
   *  ctrl-or-cmd-held`. When true, every round-eligible corner
   *  handle on every line renders filled (highlighted) to signal
   *  that a commit will apply the radius to all of them; when
   *  false only corners that already carry a rounding entry render
   *  filled. */
  setRoundModeAll(all: boolean) {
    if (this.roundModeAll === all) return;
    this.roundModeAll = all;
    if (this.showCornerHandles && this.project) this.render(this.project);
  }

  /** Push a transient corner-handle override from the active Round
   *  drag (issue #100), or `null` to clear. Re-renders the affected
   *  line's corner-handles group in place so the handle dot can
   *  follow the cursor per-frame without re-rendering the whole
   *  scene. Clearing fires the same in-place refresh so the dot
   *  snaps back to its committed position. */
  setTransientCornerHandle(
    spec: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      radius: number;
      applyToAll: boolean;
    } | null,
  ): void {
    const prev = this.transientCornerHandle;
    this.transientCornerHandle = spec;
    // Refresh the line(s) whose handles need to redraw — the
    // newly-overridden line and (when switching corners) the
    // previously-overridden one. Idempotent when both are the same.
    const touched = new Set<string>();
    if (prev) touched.add(prev.constructionId);
    if (spec) touched.add(spec.constructionId);
    for (const id of touched) this.refreshCornerHandles(id);
  }

  /** Push transient pathData overrides for offset lines that follow
   *  the source line currently being rounded (issue #100). Each
   *  affected offset line's rendered geometry replaces its committed
   *  pathData with the supplied preview pathData; passing `null`
   *  reverts them all back to the committed geometry. Re-renders
   *  the matching line items in place so the offset visibly tracks
   *  the rounded outline as the user drags. */
  setTransientRoundedOffsets(
    updates: ReadonlyArray<{ id: string; pathData: string }> | null,
  ): void {
    const prev = this.transientOffsetPaths;
    const next = new Map<string, string>();
    if (updates) for (const u of updates) next.set(u.id, u.pathData);
    this.transientOffsetPaths = next.size > 0 ? next : null;
    const touched = new Set<string>();
    if (prev) for (const id of prev.keys()) touched.add(id);
    for (const id of next.keys()) touched.add(id);
    for (const id of touched) this.refreshConstructionLineGeometry(id);
  }

  /** Re-render the visible line item + halo + (if applicable) the
   *  sharp-corner hairline for one construction line, in place. Reads
   *  the transient offset-path override (if any) so a Round drag can
   *  update derived offset lines without a full scene render. */
  private refreshConstructionLineGeometry(constructionId: string): void {
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    const line = this.project
      ? projectConstructionLines(this.project).find(
          (l) => l.id === constructionId,
        )
      : undefined;
    if (!line) return;
    if (!isVisible(line)) return;
    // Resolve which subitems to redraw — the line, its halo, the
    // sharp-corner hairline (if any). Hold their indices so we
    // insert the replacements at the same depth, preserving the
    // existing z-order against neighbouring lines / overlays.
    let lineIndex = -1;
    let haloIndex = -1;
    let hairlineIndex = -1;
    const children = conGroup.children ?? [];
    for (let i = 0; i < children.length; i++) {
      const c = children[i]!;
      if (
        c.data?.kind === "construction" &&
        c.data.constructionId === constructionId
      ) {
        lineIndex = i;
      } else if (
        c.data?.kind === "construction-halo" &&
        c.data.constructionId === constructionId
      ) {
        haloIndex = i;
      } else if (
        c.data?.kind === "construction-sharp-hairline" &&
        c.data.constructionId === constructionId
      ) {
        hairlineIndex = i;
      }
    }
    if (lineIndex < 0) return;
    const isSel = (() => {
      const ids =
        this.selection.constructionIds &&
        this.selection.constructionIds.length > 0
          ? this.selection.constructionIds
          : this.selection.constructionId
            ? [this.selection.constructionId]
            : [];
      return ids.includes(constructionId);
    })();
    // Apply transient pathData override (if any) by temporarily
    // patching the line we hand to the builders. Keeps the model
    // untouched — the rebuilt items render the override but the
    // committed `line.pathData` is unchanged.
    const override = this.transientOffsetPaths?.get(constructionId);
    const effectiveLine: ConstructionLine =
      override !== undefined ? { ...line, pathData: override } : line;
    // Remove and rebuild in reverse-index order so earlier indices
    // remain valid as we delete. Re-insert at the original index
    // so neighbour z-order is preserved.
    const toReplace: Array<{
      index: number;
      builder: () => paper.Item | null;
    }> = [];
    toReplace.push({
      index: lineIndex,
      builder: () =>
        this.constructionOverlayRenderer.buildConstructionLineItem(
          effectiveLine,
          isSel,
        ),
    });
    if (haloIndex >= 0) {
      toReplace.push({
        index: haloIndex,
        builder: () =>
          this.constructionOverlayRenderer.buildConstructionLineHalo(
            effectiveLine,
            isSel,
          ),
      });
    }
    if (hairlineIndex >= 0) {
      toReplace.push({
        index: hairlineIndex,
        builder: () => this.buildSharpCornerHairline(effectiveLine),
      });
    }
    // Sort high → low so each removal leaves the lower indices
    // unchanged for the next iteration.
    toReplace.sort((a, b) => b.index - a.index);
    for (const { index, builder } of toReplace) {
      const existing = children[index];
      if (!existing) continue;
      existing.remove();
      const fresh = builder();
      if (fresh) conGroup.insertChild(index, fresh);
    }
  }

  /** Re-render the corner-handles subgroup for one construction
   *  line in place. Cheaper than a full scene render, called by
   *  `setTransientCornerHandle` so the handle dot can follow the
   *  cursor per-frame. */
  private refreshCornerHandles(constructionId: string): void {
    if (!this.showCornerHandles) return;
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    // Drop the previous handles subgroup (if any) for this line.
    for (const sub of (conGroup.children ?? []).slice()) {
      if (
        sub.data?.kind === "construction-corner-handles" &&
        sub.data.constructionId === constructionId
      ) {
        sub.remove();
      }
    }
    const line = this.project
      ? projectConstructionLines(this.project).find(
          (l) => l.id === constructionId,
        )
      : undefined;
    if (!line) return;
    if (line.offsetSource) return;
    const fresh = this.constructionOverlayRenderer.buildCornerHandles(line);
    if (fresh) conGroup.addChild(fresh);
  }

  setMatVisible(visible: boolean) {
    if (this.matVisible === visible) return;
    this.matVisible = visible;
    if (this.project) this.render(this.project);
  }

  isMatVisible(): boolean {
    return this.matVisible;
  }

  /** While a saved project loads (#1230), lay the empty mat out where the
   *  project's mat will be, so nothing shifts when it arrives. Null goes back
   *  to the default empty mat. `render: false` leaves the redraw to the caller
   *  (the restore, which renders the arriving project next). */
  /** Open (or close) a LOAD_BAR_PX strip in the mat just below the grid's
   *  top line, for the delayed-load progress bar to show through (#1230). */
  setLoadingStrip(open: boolean) {
    this.matRenderer.setLoadingStrip(open);
    if (this.project) this.render(this.project);
  }

  setLoadingMatLayout(layout: MatLayout | null, render = true) {
    this.matRenderer.setLoadingLayout(layout);
    if (render && this.project) this.render(this.project);
  }

  /** The mat placement resolved by the last render (#785 follow-up), so the
   *  app can persist it on the project and restore it on reload. Null when
   *  there's no established layout yet (empty project / mat hidden). Returns a
   *  copy so callers can't mutate the live cache. */
  getMatLayout(): MatLayout | null {
    return this.matRenderer.getMatLayout();
  }

  setMatUnits(units: "mm" | "in") {
    if (this.matUnits === units) return;
    this.matUnits = units;
    // Switching units invalidates the established mat layout — re-snap to
    // the new step on the next render.
    this.matRenderer.clearCachedMatLayout();
    if (this.matVisible && this.project) this.render(this.project);
  }

  /** Origin + step of the active mat's grid. Used by SelectTool to snap a
   *  dragged part's corner to nearby grid lines, and by ConstructionTool
   *  for the Grid snap on construction-line drags (issue #98 follow-up).
   *  Returns null only when the mat is hidden. Empty projects still get
   *  a grid back: the cache stays empty by design so the first part can
   *  re-snap fresh, but the visible mat layout is recomputable on demand. */
  getMatGridInfo(): {
    innerLeft: number;
    innerBottom: number;
    innerRight: number;
    innerTop: number;
    step: number;
  } | null {
    if (!this.matVisible) return null;
    let layout = this.matRenderer.peekCachedMatLayout();
    if (!layout && this.project)
      layout = this.matRenderer.computeMatLayout(this.project);
    if (!layout) return null;
    const { w, h, cx, cy } = layout;
    return {
      innerLeft: cx - w / 2 + MAT_PADDING,
      innerBottom: cy + h / 2 - MAT_PADDING,
      innerRight: cx + w / 2 - MAT_PADDING,
      innerTop: cy - h / 2 + MAT_PADDING,
      step: this.gridSteps().minor,
    };
  }

  getSelection(): SceneSelection {
    return this.selection;
  }

  /** Union bounding box (project mm) of the currently-selected objects —
   *  pieces, construction lines, and construction groups. Used by the
   *  Alt-arrow grid-align nudge (#727) to find the selection's top-left
   *  corner. Returns null when nothing with resolvable bounds is selected
   *  (a piece sub-item, or an image-only selection), so the caller falls
   *  back to a fixed-step nudge. */
  getSelectionBounds(): paper.Rectangle | null {
    const sel = this.selection;
    if (sel.decorationId || sel.holeId || sel.cutoutId || sel.stitchId) {
      return null;
    }
    let rect: paper.Rectangle | null = null;
    const add = (b: paper.Rectangle | null | undefined) => {
      if (b) rect = rect ? rect.unite(b) : b.clone();
    };
    const pieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
    for (const id of pieceIds) add(this.getPieceBounds(id));
    const lineIds =
      sel.constructionIds ?? (sel.constructionId ? [sel.constructionId] : []);
    for (const id of lineIds) add(this.getConstructionLineItem(id)?.bounds);
    for (const gid of sel.constructionGroupIds ?? []) {
      add(this.getConstructionGroupOverlayItem(gid)?.bounds);
    }
    return rect;
  }

  /** Read-only projection of the current selection into kind-tagged
   *  `ObjectRef`s (issue #545, Phase 2) — construction lines, groups, and
   *  tracing images as one list, so generic construction-object machinery
   *  can consume the selection without re-deriving it from the three
   *  separate axes. The axes (`constructionIds` / `constructionGroupIds` /
   *  `tracingImageIds`) remain the source of truth; this only reflects
   *  them. Lead/`*Id` shorthands are ignored — the normalized `*Ids` sets
   *  are authoritative after `setSelection`. */
  selectedObjects(): ObjectRef[] {
    const sel = this.selection;
    const refs: ObjectRef[] = [];
    for (const id of sel.constructionIds ?? []) refs.push(lineRef(id));
    for (const id of sel.constructionGroupIds ?? []) refs.push(groupRef(id));
    for (const id of sel.tracingImageIds ?? []) refs.push(imageRef(id));
    return refs;
  }

  /** When exactly one construction line (and nothing else of
   *  consequence) is selected, return its id; otherwise null. Drives
   *  the rotation-overlay gating in renderPiece — the handle only
   *  appears for solo-line selections so a multi-pick doesn't fight
   *  the group bbox. */
  singleSelectedConstructionLineId(): string | null {
    const sel = this.selection;
    const ids =
      sel.constructionIds ?? (sel.constructionId ? [sel.constructionId] : []);
    if (ids.length !== 1) return null;
    if ((sel.constructionGroupIds ?? []).length > 0) return null;
    if (
      sel.pieceId ||
      sel.decorationId ||
      sel.holeId ||
      sel.cutoutId ||
      sel.stitchId
    ) {
      return null;
    }
    return ids[0]!;
  }

  /** Mirror of `singleSelectedConstructionLineId` for groups: returns
   *  the id of the single selected construction group (with no other
   *  competing selection), or null. Drives the group-rotation
   *  overlay's eligibility check. */
  singleSelectedConstructionGroupId(): string | null {
    const sel = this.selection;
    const ids = sel.constructionGroupIds ?? [];
    if (ids.length !== 1) return null;
    if (
      sel.pieceId ||
      sel.decorationId ||
      sel.holeId ||
      sel.cutoutId ||
      sel.stitchId
    ) {
      return null;
    }
    return ids[0]!;
  }

  /** Translate every rendered item belonging to one construction
   *  GROUP (its member lines' yellow paths / halos / hairlines /
   *  handles, the group's bbox overlay) by the same delta. Mirrors
   *  the per-line `translateConstructionLineRender` but resolved at
   *  the group level via `descendantConstructionLineIds`. */
  rotateRenderedGroupItems(
    groupId: string,
    deg: number,
    cx: number,
    cy: number,
  ): void {
    this.transientTransform.rotateRenderedGroupItems(groupId, deg, cx, cy);
  }

  getPaperScope(): paper.PaperScope {
    return this.scope;
  }

  /** MatRenderHost: the mat renderer draws onto the same root layer + paper
   *  scope the Scene owns, and reads the active units + grid steps that stay
   *  single-definition on Scene. */
  getRootLayer(): paper.Layer {
    return this.rootLayer;
  }

  getMatUnits(): "mm" | "in" {
    return this.matUnits;
  }

  /** ConstructionOverlayHost: the overlay builders draw onto the same paper
   *  scope Scene owns and read the live project + selection / hover / round
   *  state, the drag-distance formatter, and the rotation-overlay geometry
   *  constants — all kept single-definition on Scene. */
  getProject(): Project | null {
    return this.project;
  }

  getTransientCornerHandle(): {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    radius: number;
    applyToAll: boolean;
  } | null {
    return this.transientCornerHandle;
  }

  getRoundPresetRadius(): number | null {
    return this.roundPresetRadius;
  }

  isRoundModeAll(): boolean {
    return this.roundModeAll;
  }

  constructionRotationHandlesEnabled(): boolean {
    return this.showConstructionRotationHandles;
  }

  getLineRotationHoverCorner(): {
    constructionId: string;
    corner: "tl" | "tr" | "bl" | "br";
  } | null {
    return this.lineRotationHoverCorner;
  }

  getGroupRotationHoverCorner(): {
    constructionGroupId: string;
    corner: "tl" | "tr" | "bl" | "br";
  } | null {
    return this.groupRotationHoverCorner;
  }

  lineRotationHandleOffsetMm(): number {
    return Scene.LINE_ROTATION_HANDLE_OFFSET_MM;
  }

  lineRotationHandleRadiusMm(): number {
    return Scene.LINE_ROTATION_HANDLE_RADIUS_MM;
  }

  lineResizeHandleHalfMm(): number {
    return Scene.LINE_RESIZE_HANDLE_HALF_MM;
  }

  lineRotationCornerZoneOuterMm(): number {
    return Scene.LINE_ROTATION_CORNER_ZONE_OUTER_MM;
  }

  lineRotationArrowArmMm(): number {
    return Scene.LINE_ROTATION_ARROW_ARM_MM;
  }

  getLibrary(): StampLibrary {
    return this.library;
  }

  getStampSymbols(): StampSymbols {
    return this.symbols;
  }

  /** Project-space bounding box of a piece's rendered group (shape + holes +
   *  cutouts + decorations). Returns null if the piece isn't on screen yet. */
  getPieceBounds(pieceId: string): paper.Rectangle | null {
    for (const child of this.rootLayer.children) {
      if (child.data?.kind === "piece" && child.data.pieceId === pieceId) {
        return child.bounds;
      }
    }
    return null;
  }

  /** Returns the rendered Paper.js group for a piece, or null. Used by tools
   *  that want to apply a transient transform during a drag without going
   *  through history. */
  getPieceGroup(pieceId: string): paper.Item | null {
    for (const child of this.rootLayer.children) {
      if (child.data?.kind === "piece" && child.data.pieceId === pieceId) {
        // Callers transiently transform the returned group outside the render
        // pipeline, so drop it from the incremental cache (#579) — the next
        // render must rebuild it from the model, not reuse the mutated group.
        this.pieceGroupCache.delete(pieceId);
        return child;
      }
    }
    return null;
  }

  /** Returns the rendered hole circle for a (pieceId, holeId). Only present
   *  in structural edit mode; null otherwise. */
  getHoleItem(pieceId: string, holeId: string): paper.Item | null {
    const piece = this.getPieceGroup(pieceId);
    if (!piece || !piece.children) return null;
    for (const child of piece.children) {
      if (child.data?.kind === "hole" && child.data.holeId === holeId) {
        return child;
      }
    }
    return null;
  }

  /** Returns the rendered cutout path for a (pieceId, cutoutId). */
  getCutoutItem(pieceId: string, cutoutId: string): paper.Item | null {
    const piece = this.getPieceGroup(pieceId);
    if (!piece || !piece.children) return null;
    for (const child of piece.children) {
      if (child.data?.kind === "cutout" && child.data.cutoutId === cutoutId) {
        return child;
      }
    }
    return null;
  }

  /** Set / clear the currently-hovered construction segment. Driven
   *  by the properties panel's segment list (mouseenter / mouseleave
   *  on each row) — the matching curve segment on the canvas gets a
   *  marching-ants overlay so the user can find it at a glance. */
  setConstructionSegmentHover(
    spec: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    } | null,
  ): void {
    this.constructionHover.setConstructionSegmentHover(spec);
  }

  /** Set / clear the currently-hovered segment-list dot. Parallel
   *  to `setConstructionDeleteHover` but renders a yellow overlay
   *  (cycle-mode color) and always includes a ring around the
   *  affected anchor — the user expects the dot hover to surface
   *  the specific anchor + its handle on the canvas, not the
   *  entire curve segment the row represents. */
  setConstructionPanelHover(
    spec:
      | {
          kind: "anchor";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        }
      | {
          kind: "handle";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
          side: "in" | "out";
        }
      | {
          kind: "both-handles";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        }
      | null,
  ): void {
    this.constructionHover.setConstructionPanelHover(spec);
  }

  /** Set / clear the constraint currently being hovered in the
   *  properties / layers panel. Surfaces every feature the
   *  constraint references at once (yellow ring per anchor,
   *  marching-ants per curve, halo per whole line) so the user
   *  can see "this constraint connects these things". */
  setConstructionConstraintHover(spec: { refs: ConstraintRef[] } | null): void {
    this.constructionHover.setConstructionConstraintHover(spec);
  }

  /** Set / clear the drag-time snap-candidate highlight. Drawn as
   *  a yellow ring on each of the two anchors (source + candidate)
   *  to flag that releasing the drag will pin them with a
   *  coincident constraint. Driven by the Construction tool's
   *  per-frame snap check. */
  setConstructionSnapCandidateHover(
    spec: {
      source?: SnapCandidateRef;
      candidate: SnapCandidateRef;
    } | null,
  ): void {
    this.constructionHover.setConstructionSnapCandidateHover(spec);
  }

  /** Show (or hide) a free-floating yellow snap target at an
   *  arbitrary project-mm point. Used by the Construction Draw
   *  sub-mode to preview where a click would land when grid snap
   *  fires on empty space (no nearby anchor). Pass `null` to clear.
   *  Mutually exclusive with the anchor / centre / midpoint
   *  candidate ring — only one should be lit at a time. */
  setConstructionDraftSnapPoint(pt: { x: number; y: number } | null): void {
    this.constructionHover.setConstructionDraftSnapPoint(pt);
  }

  /** Set / clear the Offset hover affordance (issue #148) — the
   *  perpendicular arrow-with-tick drawn next to the cursor while it
   *  hovers an offsettable edge (a construction line in the Offset
   *  sub-tool, or a part / cutout / hole edge in the leather Stitch
   *  tool, #768). Pass `null` to clear. */
  setOffsetHoverAffordance(
    spec: {
      x: number;
      y: number;
      dirX: number;
      dirY: number;
      tanX: number;
      tanY: number;
    } | null,
  ): void {
    this.constructionHover.setOffsetHoverAffordance(spec);
  }

  /** Set / clear the bezier-toggle hover affordance (#147) — the glyph drawn
   *  next to the cursor while it hovers a point a click would toggle between
   *  a corner and a curve. Pass `null` to clear. */
  setBezierToggleAffordance(pt: { x: number; y: number } | null): void {
    this.constructionHover.setBezierToggleAffordance(pt);
  }

  /** Inspect the current free-floating draft snap target — used by
   *  the e2e suite to verify the hover ring landed at the expected
   *  grid intersection without scraping the rendered Paper items. */
  getConstructionDraftSnapPoint(): { x: number; y: number } | null {
    return this.constructionHover.getConstructionDraftSnapPoint();
  }

  /** Show (or hide) the in-draw snap indicator for the leather Draw tools
   *  (#761) at an arbitrary project-mm point — where the cursor has snapped to
   *  an existing vertex or the grid while drawing. Pass `null` to clear. */
  setDrawSnapPoint(pt: { x: number; y: number } | null): void {
    if (this.drawSnapPoint?.x === pt?.x && this.drawSnapPoint?.y === pt?.y) {
      return;
    }
    this.drawSnapPoint = pt ? { x: pt.x, y: pt.y } : null;
    this.refreshDrawSnapPoint();
  }

  /** Inspect the current in-draw snap indicator position — used by the e2e
   *  suite to verify snapping without scraping rendered Paper items. */
  getDrawSnapPoint(): { x: number; y: number } | null {
    return this.drawSnapPoint;
  }

  private refreshDrawSnapPoint(): void {
    if (this.drawSnapPointItem) this.drawSnapPointItem.remove();
    this.drawSnapPointItem = null;
    const pt = this.drawSnapPoint;
    if (!pt) return;
    this.scope.activate();
    const ring = new this.scope.Path.Circle(
      new this.scope.Point(pt.x, pt.y),
      2.0,
    );
    const fill = new this.scope.Color(cssVar("--canvas-accent"));
    fill.alpha = 0.4;
    ring.fillColor = fill;
    ring.strokeColor = new this.scope.Color(cssVar("--canvas-accent"));
    ring.strokeWidth = 2.2;
    ring.strokeScaling = false;
    ring.data = { kind: "draw-snap-point", ephemeral: true };
    this.drawSnapPointItem = ring;
  }

  /** Toggle the persistent crosshair markers that sit over every
   *  pair of anchors pinned by a coincident (Snap) constraint —
   *  one crosshair per pair, drawn at the pair's shared position.
   *  Called by the Construction tool on activation / deactivation
   *  so the markers stay scoped to the tool that cares. */
  setConstructionCrosshairsVisible(visible: boolean): void {
    this.constructionHover.setConstructionCrosshairsVisible(visible);
  }

  /** Force a crosshair rebuild from the *live* rendered geometry —
   *  called per-frame during a Construction drag so the markers
   *  travel with the rigidly-translated partner lines instead of
   *  staying frozen until commit. */
  refreshConstructionCrosshairsLive(): void {
    this.constructionHover.refreshConstructionCrosshairsLive();
  }

  /** Format a project-mm distance for the drag guide label in the active
   *  mat unit (mm to 0.1, inches to 0.001"). */
  formatDragDistance(mm: number): string {
    return this.matUnits === "in"
      ? `${(mm / 25.4).toFixed(3)} in`
      : `${mm.toFixed(1)} mm`;
  }

  /** Drag guide (#725): the move vector from the drag's start point (`from`)
   *  to the translated endpoint (`to`) — a small dot at each end, a dashed
   *  line between them, an arrowhead at `to`, and the distance (|from→to|) in
   *  the active unit. Shown for any drag (not just angle-snapped ones).
   *  Idempotent — replaces any existing guide. Cleared by
   *  `clearDragAxisGuide`. */
  setDragAxisGuide(
    from: { x: number; y: number },
    to: { x: number; y: number },
    opts: { overLeather?: boolean } = {},
  ): void {
    this.clearDragAxisGuide();
    this.scope.activate();
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length < 1e-6) return;
    const ux = dx / length;
    const uy = dy / length;
    const zoom = this.scope.view.zoom || 1;
    // Over a leather piece the light accent washes out — darken (matches the
    // guide-ray accent). Test the drag origin so a move that starts on leather
    // reads clearly, while a drag over the bare mat keeps the light accent.
    // `opts.overLeather` forces the dark accent: a drag that MOVES a leather
    // object belongs to that object whatever the anchor happens to sit on — on
    // a curved part the frame's corner legitimately falls outside the curve, and
    // the ray flipping pale there just read as a glitch (#971 follow-up).
    const color = new this.scope.Color(
      opts.overLeather || this.isPointOverLeather(from)
        ? LEATHER_SELECTION_STROKE()
        : SELECTION_STROKE(),
    );
    const P = this.scope.Point;
    const group = new this.scope.Group();
    group.data = { kind: "drag-axis-guide", ephemeral: true };

    // Dashed line from the start point to the destination only (no extension
    // past the destination).
    const ray = new this.scope.Path.Line(
      new P(from.x, from.y),
      new P(to.x, to.y),
    );
    ray.strokeColor = color;
    ray.strokeWidth = 1;
    ray.strokeScaling = false;
    ray.dashArray = [4, 3];
    ray.data = { ephemeral: true };
    group.addChild(ray);

    // Origin dot at the drag's start point — a fixed ~3px on screen.
    const dot = new this.scope.Path.Circle(new P(from.x, from.y), 3 / zoom);
    dot.fillColor = color;
    dot.data = { ephemeral: true };
    group.addChild(dot);

    const px = -uy;
    const py = ux;
    // Arrowhead at the translation endpoint, pointing along the drag. Sized in
    // screen px (divided by zoom) so it stays constant on screen.
    const ah = 11 / zoom;
    const aw = 5 / zoom;
    const backX = to.x - ux * ah;
    const backY = to.y - uy * ah;
    const head = new this.scope.Path([
      new P(to.x, to.y),
      new P(backX + px * aw, backY + py * aw),
      new P(backX - px * aw, backY - py * aw),
    ]);
    head.closed = true;
    head.fillColor = color;
    head.data = { ephemeral: true };
    group.addChild(head);

    // Endpoint dot — same size as the origin dot — drawn on top of the
    // arrowhead so the guide reads as "this point moved from one dot to the
    // other."
    const endDot = new this.scope.Path.Circle(new P(to.x, to.y), 3 / zoom);
    endDot.fillColor = color;
    endDot.data = { ephemeral: true };
    group.addChild(endDot);

    // Distance label at the midpoint of the translation vector, offset to
    // one side so it clears the ray, arrow, and the moving object.
    try {
      const midX = (from.x + to.x) / 2;
      const midY = (from.y + to.y) / 2;
      const label = new this.scope.PointText({
        point: new P(midX + px * (14 / zoom), midY + py * (14 / zoom)),
        content: this.formatDragDistance(length),
        fillColor: color,
        fontFamily: "sans-serif",
        fontSize: 12 / zoom,
        fontWeight: 700,
        justification: "center",
      });
      label.data = { ephemeral: true };
      group.addChild(label);
    } catch {
      /* text unavailable — dot + ray + arrow still render */
    }

    this.dragAxisGuideItem = group;
  }

  clearDragAxisGuide(): void {
    if (this.dragAxisGuideItem) {
      this.dragAxisGuideItem.remove();
      this.dragAxisGuideItem = null;
    }
  }

  /** The measurement the ruler is showing (#155), or null. Kept as STATE
   *  rather than drawn once, because a measurement outlives the gesture that
   *  made it — you take it in order to read it — and any commit elsewhere
   *  rebuilds the scene and would otherwise wipe it. */
  private measurement: {
    a: Vec2;
    b: Vec2;
    /** Whether each end landed ON something (a vertex, an edge, a crossing,
     *  the grid) rather than wherever the cursor happened to be. Drawn as the
     *  snap ring, so the reading can be trusted at a glance: "42.0 mm" means
     *  something different when both ends are pinned to corners than when
     *  they're two arbitrary spots. */
    aSnapped?: boolean;
    bSnapped?: boolean;
  } | null = null;
  private measurementItem: paper.Item | null = null;

  setMeasurement(
    spec: { a: Vec2; b: Vec2; aSnapped?: boolean; bSnapped?: boolean } | null,
  ): void {
    this.measurement = spec
      ? {
          a: { ...spec.a },
          b: { ...spec.b },
          aSnapped: !!spec.aSnapped,
          bSnapped: !!spec.bSnapped,
        }
      : null;
    this.refreshMeasurement();
  }

  /** Test hook + panel source: the current measurement and its length in mm. */
  getMeasurement(): {
    a: Vec2;
    b: Vec2;
    lengthMm: number;
    aSnapped: boolean;
    bSnapped: boolean;
  } | null {
    const m = this.measurement;
    if (!m) return null;
    return {
      a: { ...m.a },
      b: { ...m.b },
      lengthMm: Math.hypot(m.b.x - m.a.x, m.b.y - m.a.y),
      aSnapped: !!m.aSnapped,
      bSnapped: !!m.bSnapped,
    };
  }

  /** Draw the ruler's line: a dot at each end, the span between them, and the
   *  distance in the active unit. Deliberately the same vocabulary as the drag
   *  guide (#725) — dots, a thin line, a bold mid-span label — since both
   *  answer "how far is it from here to there". */
  refreshMeasurement(): void {
    if (this.measurementItem) {
      this.measurementItem.remove();
      this.measurementItem = null;
    }
    const m = this.measurement;
    if (!m) return;
    this.scope.activate();
    const P = this.scope.Point;
    const zoom = this.scope.view.zoom || 1;
    const color = new this.scope.Color(cssVar("--canvas-accent"));
    const group = new this.scope.Group();
    group.data = { kind: "measurement", ephemeral: true };
    const a = new P(m.a.x, m.a.y);
    const b = new P(m.b.x, m.b.y);
    const span = new this.scope.Path({ segments: [a, b] });
    span.strokeColor = color;
    span.strokeWidth = 1.6;
    span.strokeScaling = false;
    span.data = { kind: "measurement", ephemeral: true };
    group.addChild(span);
    for (const at of [a, b]) {
      const dot = new this.scope.Path.Circle(at, 3 / zoom);
      dot.fillColor = color;
      dot.data = { kind: "measurement", ephemeral: true };
      group.addChild(dot);
    }
    // Snap rings on whichever ends landed on something — the same ring the
    // draw tools show while snapping (`refreshDrawSnapPoint`), because it
    // means the same thing here and a second visual vocabulary for one idea
    // is a thing to learn for nothing.
    for (const [at, snapped] of [
      [a, m.aSnapped] as const,
      [b, m.bSnapped] as const,
    ]) {
      if (!snapped) continue;
      const ring = new this.scope.Path.Circle(at, 2.0);
      const fill = new this.scope.Color(cssVar("--canvas-accent"));
      fill.alpha = 0.4;
      ring.fillColor = fill;
      ring.strokeColor = new this.scope.Color(cssVar("--canvas-accent"));
      ring.strokeWidth = 2.2;
      ring.strokeScaling = false;
      ring.data = { kind: "measurement-snap", ephemeral: true };
      group.addChild(ring);
    }
    const len = b.getDistance(a);
    // End ticks perpendicular to the span, so a measurement between two points
    // on a line is distinguishable from the line itself.
    if (len > 1e-6) {
      const ux = (b.x - a.x) / len;
      const uy = (b.y - a.y) / len;
      const half = 5 / zoom;
      for (const at of [a, b]) {
        const tick = new this.scope.Path({
          segments: [
            new P(at.x - uy * half, at.y + ux * half),
            new P(at.x + uy * half, at.y - ux * half),
          ],
        });
        tick.strokeColor = color;
        tick.strokeWidth = 1.6;
        tick.strokeScaling = false;
        tick.data = { kind: "measurement", ephemeral: true };
        group.addChild(tick);
      }
      try {
        const label = new this.scope.PointText({
          point: new P(
            (a.x + b.x) / 2 - uy * (14 / zoom),
            (a.y + b.y) / 2 + ux * (14 / zoom),
          ),
          content: this.formatDragDistance(len),
          fillColor: color,
          fontFamily: "sans-serif",
          fontSize: 12 / zoom,
          fontWeight: 700,
          justification: "center",
        });
        label.data = { kind: "measurement", ephemeral: true };
        group.addChild(label);
      } catch {
        /* text unavailable — the line and its dots still read */
      }
    }
    this.rootLayer.addChild(group);
    this.measurementItem = group;
  }

  /** Master on/off for guide rays (#745). When turned off, any drawn rays are
   *  cleared and subsequent `setGuideRays` calls render nothing until re-enabled. */
  setRaysEnabled(on: boolean): void {
    if (this.raysEnabled === on) return;
    this.raysEnabled = on;
    if (!on) this.clearGuideRays();
  }

  isRaysEnabled(): boolean {
    return this.raysEnabled;
  }

  /**
   * Draw a set of value rays (#744) — dashed lines from an origin to a point,
   * each with an arrowhead and a value label (angle in °, length / distance /
   * radius in the mat unit). Used by the drawing / editing tools to show the
   * value they're setting right on the canvas. Idempotent (replaces any prior
   * rays); pass `null` / `[]` to clear. No-op while rays are disabled (#745).
   */
  setGuideRays(rays: GuideRay[] | null): void {
    this.clearGuideRays();
    if (!this.raysEnabled || !rays || rays.length === 0) return;
    this.scope.activate();
    const group = new this.scope.Group();
    group.data = { kind: "guide-rays", ephemeral: true };
    for (const r of rays)
      this.constructionOverlayRenderer.renderGuideRay(group, r);
    if (!group.children || group.children.length === 0) {
      group.remove();
      return;
    }
    this.guideRaysItem = group;
  }

  clearGuideRays(): void {
    if (this.guideRaysItem) {
      this.guideRaysItem.remove();
      this.guideRaysItem = null;
    }
  }

  /** Whether a canvas point lies over any visible leather piece. Decides the
   *  ray accent: the pale leather fill washes out the light `--canvas-accent`,
   *  so a ray whose origin sits on leather uses the darker
   *  `--canvas-accent-leather` (meets WCAG contrast), while one over the mat
   *  keeps the light accent. Cheap enough for the drag-frequency it runs at. */
  isPointOverLeather(pt: { x: number; y: number }): boolean {
    // In the construction-editing context parts render as an unfilled scaffold
    // over the mat, so there's no pale fill to wash the light accent out.
    if (!this.project || this.constructionActive) return false;
    const P = new this.scope.Point(pt.x, pt.y);
    for (const piece of this.project.pieces) {
      if (!isVisible(piece)) continue;
      let path: paper.CompoundPath;
      try {
        path = new this.scope.CompoundPath({
          pathData: piece.shapePathData,
          insert: false,
        });
      } catch {
        continue;
      }
      // A point exactly ON the outline counts as over the leather. `contains`
      // is false on the boundary, and the points this is asked about are very
      // often exactly there — a drag guide anchored on a part's corner or edge
      // midpoint — which otherwise picked the pale off-leather accent while
      // sitting right on the part (#971 follow-up). The tolerance only needs to
      // cover the boundary itself plus round-trip float error.
      const ON_EDGE_TOL_MM = 0.05;
      const onEdge =
        (path.getNearestPoint(P)?.getDistance(P) ?? Infinity) <= ON_EDGE_TOL_MM;
      const inside = path.contains(P) || onEdge;
      path.remove();
      if (!inside) continue;
      // A point strictly inside a VOID isn't over leather at all — there's no
      // pale fill there to wash the light accent out, so it should keep the mat
      // accent (#972). Only when the void is actually rendered as a void: the
      // structural edit mode (Hole / Cutout) draws full leather with the
      // hole / cutout as a marker ON TOP, so the fill is still there and the
      // dark accent is still right. Display mode renders leather MINUS its
      // voids, and that's when the mat shows through.
      // A point on the piece outline itself is leather regardless — it's the
      // boundary of the material, not of a void.
      if (
        !onEdge &&
        this.editMode !== "structural" &&
        this.pointInVoid(piece, P)
      )
        continue;
      return true;
    }
    return false;
  }

  /** Whether `P` lies strictly inside one of `piece`'s visible holes or
   *  cutouts — the parts of its bounding outline where the mat shows through
   *  once the piece is rendered minus its voids. A point exactly on a void's
   *  own boundary counts as leather (it's the rim), matching how the outline
   *  boundary is treated above. */
  private pointInVoid(piece: Piece, P: paper.Point): boolean {
    for (const hole of piece.holes) {
      if (!isVisible(hole)) continue;
      const d = Math.hypot(P.x - hole.position.x, P.y - hole.position.y);
      if (d < hole.radius - 1e-6) return true;
    }
    for (const cut of piece.cutouts) {
      if (!isVisible(cut)) continue;
      let path: paper.CompoundPath;
      try {
        path = new this.scope.CompoundPath({
          pathData: cut.pathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const ON_EDGE_TOL_MM = 0.05;
      const onRim =
        (path.getNearestPoint(P)?.getDistance(P) ?? Infinity) <= ON_EDGE_TOL_MM;
      const inVoid = !onRim && path.contains(P);
      path.remove();
      if (inVoid) return true;
    }
    return false;
  }

  /** Set / clear the currently-hovered Delete sub-sub-tool target.
   *  Driven by `ConstructionTool.refreshEditCursor` whenever the
   *  cursor is over (or leaves) a deletable thing in delete mode. */
  setConstructionDeleteHover(spec: ConstructionDeleteHoverSpec | null): void {
    this.constructionHover.setConstructionDeleteHover(spec);
  }

  /** Returns the rendered path for a project-level construction line.
   *  Walks the root layer for the `construction-group` then matches the
   *  child by id. Translating it during a drag moves the visual until
   *  the model commit triggers a re-render. */
  getConstructionLineItem(constructionId: string): paper.Item | null {
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        if (
          sub.data?.kind === "construction" &&
          sub.data.constructionId === constructionId
        ) {
          return sub;
        }
      }
    }
    return null;
  }

  /** Return the live Paper.Item for the dark halo that sits beneath
   *  the yellow stroke of one construction line, or null when the
   *  line has no current halo (single-line lists below the auto-
   *  section render the line but no halo, for example). Only used
   *  by e2e tests today — production code paths go through
   *  `translateConstructionLineRender` instead. */
  getConstructionLineHaloItem(constructionId: string): paper.Item | null {
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        if (
          sub.data?.kind === "construction-halo" &&
          sub.data.constructionId === constructionId
        ) {
          return sub;
        }
      }
    }
    return null;
  }

  /** Re-derive the dark halo's pathData from the live yellow-stroke
   *  pathData. Used during per-frame edits that distort the line
   *  geometry rather than translate it rigidly (vertex drag, curve-
   *  bend, bezier-handle drag) — the halo is a sibling of the main
   *  path that doesn't share the same Segments, so without this
   *  pass the halo stays at the pre-drag shape until the next full
   *  re-render. Same lag class as the issue #85 translate fix; this
   *  is the non-translate variant. No-op when the line has no halo
   *  rendered (defensive — a line might be rendered without one in
   *  some panel contexts). */
  refreshConstructionLineHalo(constructionId: string): void {
    let mainItem: paper.PathItem | null = null;
    let haloItem: paper.PathItem | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        const kind = sub.data?.kind;
        if (sub.data?.constructionId !== constructionId) continue;
        if (kind === "construction") mainItem = sub as paper.PathItem;
        else if (kind === "construction-halo") haloItem = sub as paper.PathItem;
      }
    }
    if (!mainItem || !haloItem) return;
    haloItem.pathData = mainItem.pathData;
    this.refreshConstructionLineDisplays(constructionId);
  }

  /** Rebuild the selected line's rotate/resize overlay — dashed bbox,
   *  rotation knob, corner + edge resize squares — and its rotation pivot
   *  crosshair from its LIVE rendered path (#343).
   *
   *  A line being dragged directly has its whole rendered cluster translated
   *  rigidly, overlay and pivot marker included. A DERIVED line instead has
   *  its path swapped out by the mirror re-derive, which leaves both behind:
   *  select a mirror, drag it, and its stroke and halo tracked the cursor
   *  while the bbox, every handle and the pivot sat at the pre-drag position
   *  until release snapped them across.
   *
   *  Only the single selected non-offset line ever has these (see `render`),
   *  so anything else is a no-op. */
  refreshConstructionTransformOverlay(constructionId: string): void {
    if (!this.showConstructionRotationHandles || !this.project) return;
    if (this.singleSelectedConstructionLineId() !== constructionId) return;
    const line = projectConstructionLines(this.project).find(
      (l) => l.id === constructionId,
    );
    if (!line || line.offsetSource) return;
    let conGroup: paper.Item | null = null;
    let lineItem: paper.PathItem | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      conGroup = child;
      for (const sub of child.children ?? []) {
        if (
          sub.data?.kind === "construction" &&
          sub.data.constructionId === constructionId
        ) {
          lineItem = sub as paper.PathItem;
        }
      }
    }
    if (!conGroup || !lineItem?.pathData) return;
    for (const sub of (conGroup.children ?? []).slice()) {
      const kind = sub.data?.kind;
      if (sub.data?.constructionId !== constructionId) continue;
      if (
        kind === "line-rotation-overlay" ||
        kind === "rotation-pivot-marker"
      ) {
        sub.remove();
      }
    }
    // The line's stored `rotationCenter` is left as-is on purpose. The overlay
    // un-rotates the path about that centre and then tilts the resulting box
    // back about the same one, so a stale centre cancels itself out exactly
    // and the frame still lands on the live geometry — shifting it would be
    // arithmetic with no effect. `pivotPoint` needs no handling either: the
    // deriver strips it on every commit, so a mirror — the only thing this
    // refreshes — never carries a hand-placed pivot, and its marker falls out
    // of the live bbox.
    const liveLine = { ...line, pathData: lineItem.pathData };
    const fresh =
      this.constructionOverlayRenderer.buildLineRotationOverlay(liveLine);
    if (fresh) conGroup.addChild(fresh);
    // The pivot crosshair marks a world-space point rather than part of the
    // frame, and yields to a transient one while the pivot itself is being
    // dragged — the same rule `render` applies when it first draws it.
    if (!this.transientTransform.hasActivePivotCrosshair()) {
      const pivot = effectiveLinePivot(liveLine, this.scope);
      if (pivot) {
        conGroup.addChild(
          this.buildPersistentPivotCrosshair(pivot, {
            kind: "line",
            id: constructionId,
          }),
        );
      }
    }
  }

  /** Issue #326 — rebuild the trimmed yellow / halo display strokes
   *  from the live main pathData so a vertex / handle drag updates
   *  the visible stroke in lock-step with the model item. The build
   *  helpers are gated on `lineHandlesVisible(line)` which depends
   *  on `line.offsetSource`; re-resolve through the live project so
   *  the right gate fires when a transient pathData override is in
   *  play. Display children are re-inserted at the same indices so
   *  the z-order (halo-display below display-stroke, both above the
   *  strokeless halo / main path) survives every refresh. */
  private refreshConstructionLineDisplays(constructionId: string): void {
    if (!this.project) return;
    let conGroup: paper.Item | null = null;
    let mainItem: paper.PathItem | null = null;
    let displayStroke: paper.Item | null = null;
    let haloDisplay: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      conGroup = child;
      for (const sub of child.children ?? []) {
        const kind = sub.data?.kind;
        if (sub.data?.constructionId !== constructionId) continue;
        if (kind === "construction") mainItem = sub as paper.PathItem;
        else if (kind === "construction-display-stroke") displayStroke = sub;
        else if (kind === "construction-halo-display") haloDisplay = sub;
      }
    }
    if (!conGroup || !mainItem) return;
    const line = projectConstructionLines(this.project).find(
      (l) => l.id === constructionId,
    );
    if (!line || !this.lineHandlesVisible(line)) return;
    const liveLine: ConstructionLine = {
      ...line,
      pathData: mainItem.pathData,
    };
    const isSel =
      this.selection.constructionId === constructionId ||
      (this.selection.constructionIds ?? []).includes(constructionId);
    if (displayStroke) {
      const idx = conGroup.children!.indexOf(displayStroke);
      displayStroke.remove();
      const fresh =
        this.constructionOverlayRenderer.buildConstructionLineDisplayStroke(
          liveLine,
          isSel,
        );
      if (fresh) conGroup.insertChild(idx, fresh);
    }
    if (haloDisplay) {
      const idx = conGroup.children!.indexOf(haloDisplay);
      haloDisplay.remove();
      const fresh =
        this.constructionOverlayRenderer.buildConstructionLineHaloDisplay(
          liveLine,
          isSel,
        );
      if (fresh) conGroup.insertChild(idx, fresh);
    }
  }

  /** Translate the live rendered yellow stroke AND its dark halo for
   *  one construction line by `(dx, dy)`. The halo is a sibling of
   *  the main path (so it can sit *underneath* in z-order); a plain
   *  `item.translate` on the main path would leave the halo behind
   *  until the next full re-render, producing the issue #85
   *  "halo lags during drag and pops to the new position on drop"
   *  artifact. Returns true when the main item was found and moved
   *  so callers can fall through to single-item behaviour for lines
   *  that haven't been rendered yet (defensive — shouldn't happen in
   *  practice). */
  /** Hide a construction line's rendered geometry and its selection overlay
   *  (bbox frame, rotation knob, corner resize handles) for the duration of a
   *  corner-resize drag (issue #388). `ConstructionTool` draws a live stand-in
   *  each frame instead, so the resize reads as the real shape moving rather
   *  than a ghost over a frozen original. The hidden items are recreated by
   *  the next full render, which every resize-end path triggers (commit /
   *  no-op / cancel). */
  beginConstructionResizePreview(constructionId: string): void {
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        const kind = sub.data?.kind;
        if (sub.data?.constructionId !== constructionId) continue;
        if (
          kind === "construction" ||
          kind === "construction-halo" ||
          kind === "construction-display-stroke" ||
          kind === "construction-halo-display" ||
          kind === "line-rotation-overlay"
        ) {
          sub.visible = false;
        }
      }
    }
  }

  /** Write a line's in-flight geometry onto its (hidden) rendered item during
   *  a resize drag, so everything that reads LIVE rendered positions — the
   *  snap crosshair markers in particular — tracks the drag instead of
   *  freezing at the pre-drag shape until commit (#1166). The item stays
   *  hidden (the preview draws the visible stand-in); the next full render,
   *  which every resize-end path triggers, rebuilds it from the model. */
  setConstructionResizePreviewPath(
    constructionId: string,
    pathData: string,
  ): void {
    const item = this.getConstructionLineItem(
      constructionId,
    ) as paper.PathItem | null;
    if (!item || item.pathData === pathData) return;
    item.pathData = pathData;
  }

  /** Show a construction line at a live, reshaped geometry mid-gesture —
   *  used for a snapped partner that a rotation drag STRETCHES to keep two
   *  links attached (#1166), where the cumulative-translate path used for
   *  rigid partners can't express the change. Hides the line's rendered
   *  items, writes `pathData` onto its hidden main item (so the snap
   *  crosshairs, which read live rendered positions, follow), and draws a
   *  stand-in stroke in its place. Idempotent per frame. The stand-in lives
   *  in the construction group, so the next full render removes it and
   *  rebuilds the line from the model. */
  setConstructionLiveStandIn(constructionId: string, pathData: string): void {
    let conGroup: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind === "construction-group") {
        conGroup = child;
        break;
      }
    }
    if (!conGroup) return;
    this.beginConstructionResizePreview(constructionId);
    this.setConstructionResizePreviewPath(constructionId, pathData);
    this.scope.activate();
    let standIn: paper.PathItem | null = null;
    for (const sub of conGroup.children ?? []) {
      if (
        sub.data?.kind === "construction-live-stand-in" &&
        sub.data.constructionId === constructionId
      ) {
        standIn = sub as paper.PathItem;
        break;
      }
    }
    if (standIn) {
      if (standIn.pathData !== pathData) standIn.pathData = pathData;
      return;
    }
    const path = new this.scope.CompoundPath({ pathData, insert: false });
    path.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
    path.strokeColor.alpha = 0.75;
    path.strokeWidth = 1.2;
    path.strokeScaling = false;
    path.fillColor = null;
    path.data = {
      kind: "construction-live-stand-in",
      constructionId,
      ephemeral: true,
    };
    conGroup.addChild(path);
  }

  /** Topmost visible tracing image whose placed rectangle contains
   *  `pt` (project mm), or null (issue #509). Images render in array
   *  order with later ones on top, so the scan walks the array in
   *  reverse and returns the first hit. Used by the Construction
   *  tool's Select sub-mode to make images grabbable on canvas while
   *  the rendered raster stays `locked` (so drawing tools still draw
   *  straight through the reference). */
  hitTestTracingImage(pt: { x: number; y: number }): string | null {
    if (!this.project) return null;
    const images = projectTracingImages(this.project);
    for (let i = images.length - 1; i >= 0; i--) {
      const img = images[i]!;
      if (!isVisible(img)) continue;
      // Rotation-aware containment (issue #517) — the point is rotated
      // back into the image's un-rotated local frame before the test.
      if (tracingImageContainsPoint(img, pt)) return img.id;
    }
    return null;
  }

  /** Hand the trace dialog's cropped preview to the Scene (issue #510)
   *  so it renders at the image's target bounds immediately, before the
   *  full-resolution warp finishes deriving. Dropped automatically once
   *  the real raster lands. */
  primeTracingImagePreview(
    tracingImageId: string,
    canvas: HTMLCanvasElement,
  ): void {
    this.tracingImagePreview.set(tracingImageId, canvas);
  }

  /** Set a tracing image's rendered raster opacity live (issue #516)
   *  without a re-render or a history commit — drives the properties-
   *  panel opacity scrubber so dragging it fades the photo instantly.
   *  The model value is committed once on release. */
  setTracingImageOpacityLive(tracingImageId: string, opacity: number): void {
    const clamped = Math.max(0, Math.min(1, opacity));
    for (const child of this.rootLayer.children ?? []) {
      if (
        child.data?.kind === "tracing-image" &&
        child.data?.tracingImageId === tracingImageId
      ) {
        child.opacity = clamped;
        break;
      }
    }
  }

  /** Set a tracing image's rendered rotation live (issues #517 / #526)
   *  without a re-render or history commit — drives both the
   *  properties-panel rotation scrubber and the canvas rotation knob so
   *  the photo + its rotation box spin together about the image centre.
   *  The model value is committed once on release.
   *
   *  Applies the rotation as a *delta* from the previous live value
   *  (tracked in `tracingImageLiveRotation`, seeded from the committed
   *  angle) via `.rotate()`. Delta `.rotate()` keeps the raster
   *  (matrix-transformed) and the dashed overlay (a Path that bakes
   *  rotation into its points) in lock-step — the old absolute
   *  `.rotation` setter made the overlay accumulate and visibly
   *  double-rotate relative to the image (issue #526). */
  setTracingImageRotationLive(
    tracingImageId: string,
    deg: number,
    pivot?: { x: number; y: number },
  ): void {
    if (!this.project) return;
    const image = projectTracingImages(this.project).find(
      (img) => img.id === tracingImageId,
    );
    if (!image) return;
    const committed = tracingImageRotation(image);
    const prev = this.tracingImageLiveRotation.get(tracingImageId) ?? committed;
    const delta = deg - prev;
    this.tracingImageLiveRotation.set(tracingImageId, deg);
    if (Math.abs(delta) < 1e-6) return;
    // Spin about the custom pivot (#420) when supplied — an off-centre pivot
    // orbits the raster + overlay as it turns — else about the image centre.
    const center = pivot
      ? new this.scope.Point(pivot.x, pivot.y)
      : new this.scope.Point(
          image.x + image.widthMm / 2,
          image.y + image.heightMm / 2,
        );
    for (const child of this.rootLayer.children ?? []) {
      const kind = child.data?.kind;
      if (
        (kind === "tracing-image" || kind === "tracing-image-overlay") &&
        child.data?.tracingImageId === tracingImageId
      ) {
        child.rotate(delta, center);
      }
    }
  }

  /** Translate a tracing image's rendered raster + selection overlay
   *  by `(dx, dy)` for live drag feedback (issue #509), mirroring
   *  `translateConstructionLineRender`. The model `x`/`y` update lands
   *  on commit; this just keeps the on-screen photo under the cursor
   *  during the drag instead of snapping into place on release. */
  translateTracingImageRender(
    tracingImageId: string,
    dx: number,
    dy: number,
  ): boolean {
    const delta = new this.scope.Point(dx, dy);
    let moved = false;
    for (const child of this.rootLayer.children ?? []) {
      const kind = child.data?.kind;
      if (
        (kind === "tracing-image" || kind === "tracing-image-overlay") &&
        child.data?.tracingImageId === tracingImageId
      ) {
        child.translate(delta);
        if (kind === "tracing-image") moved = true;
      }
    }
    return moved;
  }

  translateConstructionLineRender(
    constructionId: string,
    dx: number,
    dy: number,
  ): boolean {
    return this.transientTransform.translateConstructionLineRender(
      constructionId,
      dx,
      dy,
    );
  }

  /** Translate every (transitive) offset-derived construction line of
   *  `sourceId` by `(dx, dy)` (issue #29 follow-up). Used by the
   *  whole-line / group drag pipelines so a moving source carries
   *  its offsets along live, instead of letting them pop into place
   *  only on commit.
   *
   *  Translation is geometry-preserving, so for whole-line drags we
   *  can just shift each derived item — there's no need to
   *  recompute the offset from the source's new pathData. Vertex /
   *  curve / handle drags need a different path; see
   *  `rederiveOffsetsOf`. */
  translateOffsetsOf(sourceId: string, dx: number, dy: number): void {
    if (!this.project) return;
    const lines = this.project.constructionLines ?? [];
    for (const line of lines) {
      if (line.offsetSource?.sourceId !== sourceId) continue;
      this.translateConstructionLineRender(line.id, dx, dy);
      this.translateOffsetsOf(line.id, dx, dy);
    }
  }

  /** Issue #180 live-mirror counterpart of `rederiveOffsetsOf`:
   *  recompute every (transitive) mirror-derived line whose
   *  `mirrorSource` references `changedId` AS EITHER its source OR
   *  its axis, swap the result into the rendered items in place.
   *  Called from every drag onMove handler so a source-vertex drag,
   *  source-whole-line drag, axis-vertex drag, or axis-whole-line
   *  drag updates the mirror live without waiting for the commit-
   *  time `deriveConstructionMirrors` pass.
   *
   *  Recurses through mirror-of-mirror chains the same way
   *  `rederiveOffsetsOf` recurses through offset-of-offset chains. */
  rederiveMirrorsAffectedBy(changedId: string): void {
    this.transientTransform.rederiveMirrorsAffectedBy(changedId);
  }

  /** Recompute every (transitive) offset-derived construction line
   *  of `sourceId` from the source's *live* rendered pathData and
   *  swap the result into the derived rendered items in place
   *  (issue #29 follow-up). Used by vertex / curve / handle drags
   *  where the source's geometry has changed shape (not just
   *  translated), so a plain item-level translate won't keep the
   *  offset correct.
   *
   *  The Paper.js item swap mutates the rendered scene without
   *  touching the project model; the commit-time
   *  `deriveConstructionOffsets` pass re-runs the same computation
   *  on the new source pathData so the persisted model stays in
   *  sync once the drag releases. */
  rederiveOffsetsOf(sourceId: string): void {
    this.transientTransform.rederiveOffsetsOf(sourceId);
  }

  /** Issue #180 unification: one entry point per drag handler that
   *  asks EVERY registered connection kind (snap / offset / mirror /
   *  …) to refresh its rendered dependents of `changedId`. Replaces
   *  the per-drag-site paired calls to `rederiveOffsetsOf` +
   *  `rederiveMirrorsAffectedBy`. New connection kinds plug in
   *  automatically via the Connections registry — no new call site
   *  needed in ConstructionTool. Snap is a no-op (its chain
   *  translation runs through `translateDragClosure` in the drag
   *  handler, not Scene). */
  rederiveConnectionsAffectedBy(changedId: string): void {
    if (!this.project) return;
    liveRederiveAllAffectedBy(this, changedId);
  }

  /** Swap the rendered `pathData` of one construction line in place
   *  (both the yellow stroke and its dark halo). Caller is the
   *  offset live-rederive path; no other code path needs to
   *  rewrite pathData mid-drag because the chain-snap pipeline
   *  only translates (geometry-preserving). */
  /** TransientTransformHost: swap the rendered pathData of one
   *  construction line in place. Public so the transient-transform
   *  controller's live mirror / offset re-derive can call it through
   *  the host; the implementation stays on Scene because it drives the
   *  Scene-private `refreshConstructionLineDisplays` rebuilder. */
  replaceConstructionLinePath(constructionId: string, pathData: string): void {
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        const kind = sub.data?.kind;
        if (
          (kind === "construction" || kind === "construction-halo") &&
          sub.data.constructionId === constructionId
        ) {
          (sub as paper.PathItem).pathData = pathData;
        }
      }
    }
    // Issue #326 — keep the trimmed display strokes in lock-step with
    // the main pathData. Cheap no-op when the line doesn't have
    // handles visible.
    this.refreshConstructionLineDisplays(constructionId);
  }

  /** Return the live Paper.Item (the wrapper Group containing the
   *  bbox rectangle + centre dot) for the selection overlay of a
   *  given construction group, or null when no overlay is currently
   *  rendered (group not selected, or no descendant lines visible).
   *  Used by `translateConstructionGroupOverlay` and by e2e tests. */
  getConstructionGroupOverlayItem(groupId: string): paper.Item | null {
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      for (const sub of child.children ?? []) {
        if (
          sub.data?.kind === "construction-group-overlay" &&
          sub.data.constructionGroupId === groupId
        ) {
          return sub;
        }
      }
    }
    return null;
  }

  /** Translate the rendered bbox + centre dot for one selected group
   *  by `(dx, dy)`. Called per-frame during a whole-group drag so the
   *  overlay tracks the moving members instead of "popping" to the
   *  new union bounds on commit (same lag class as issue #85's halo).
   *  Returns true when an overlay was found and moved so callers can
   *  silently no-op when the overlay isn't currently rendered (e.g.
   *  the user dragged a group that wasn't selected as a group). */
  translateConstructionGroupOverlay(
    groupId: string,
    dx: number,
    dy: number,
  ): boolean {
    const overlay = this.getConstructionGroupOverlayItem(groupId);
    if (!overlay) return false;
    overlay.translate(new this.scope.Point(dx, dy));
    return true;
  }

  /** Returns the rendered group for a stitch line (containing the guide
   *  polyline + hole symbols). Translating this group during a drag moves all
   *  the visuals in lock-step until the commit lands and the next render
   *  happens. */
  getStitchLineItem(pieceId: string, stitchId: string): paper.Item | null {
    const piece = this.getPieceGroup(pieceId);
    if (!piece || !piece.children) return null;
    const matches = (item: paper.Item) =>
      item.data?.kind === "stitch" && item.data.stitchId === stitchId;
    for (const child of piece.children) {
      // A direct hit (legacy structure).
      if (matches(child)) return child;
      // Stitch groups render nested inside the clipped "stitching-clip"
      // container (Scene render path), so descend one level to find them —
      // without this, live stitch dragging has no item to translate (#766
      // follow-up).
      if (child.data?.kind === "stitching-clip") {
        for (const sc of child.children ?? []) {
          if (matches(sc)) return sc;
        }
      }
    }
    return null;
  }

  /** Returns the rendered item for a decoration. Walks piece → decorations
   *  subgroup → matching child. Null if the decoration isn't visible. */
  getDecorationItem(pieceId: string, decorationId: string): paper.Item | null {
    const piece = this.getPieceGroup(pieceId);
    if (!piece || !piece.children) return null;
    for (const child of piece.children) {
      if (child.data?.kind !== "decorations") continue;
      for (const dec of child.children ?? []) {
        if (
          dec.data?.kind === "decoration" &&
          dec.data.decorationId === decorationId
        ) {
          return dec;
        }
      }
    }
    return null;
  }

  /** Hit-test in project coordinates. Returns at most one of decorationId /
   *  holeId / cutoutId along with the parent pieceId. Decorations and
   *  hole / cutout marks take priority over the piece outline since the
   *  outline is the catch-all.
   *
   *  Overlapping-handle disambiguation (issue #262): when several
   *  construction handles (bezier, vertex, rotation knob, line body,
   *  group bbox) sit within the hit tolerance of the cursor — common
   *  near a snap-anchor where two lines meet — the topmost-in-z
   *  candidate normally wins. That makes the unselected partner
   *  steal drags meant for the line the user is actively editing.
   *  We bias toward the current selection: among all hits whose
   *  owning construction line belongs to `selection.constructionIds`
   *  (or to any descendant of a selected group), return the topmost
   *  match. Falls back to plain z-order when nothing selected
   *  overlaps the cursor. The bias only kicks in when the topmost
   *  result is itself a construction kind, so an unrelated topmost
   *  (decoration, hole, piece) is never demoted. */
  hitTest(pt: paper.Point): HitTestResult {
    const matches = this.scope.project.hitTestAll(pt, {
      fill: true,
      stroke: true,
      // A finger picks from farther away (#1150).
      tolerance: hitRadiusFor(4, currentPointerKind()) / this.scope.view.zoom,
      // Skip transient previews/ghosts (e.g. the HoleTool's hover circle).
      // Otherwise they sit on top of holes and cutouts and swallow the hit.
      match: (h: paper.HitResult) => !isEphemeral(h.item),
    });
    if (!matches || matches.length === 0) return {};
    // `hitTestAll` returns matches in z-order (topmost first). Classify
    // each into a tagged result and capture the topmost match for the
    // unbiased fallback.
    let topmost: HitTestResult | null = null;
    let topmostOwnerLine: string | null = null;
    let topmostOwnerGroup: string | null = null;
    const preferIds = this.selectedConstructionLineIdSet();
    const preferGroupIds = this.selectedConstructionGroupIdSet();
    for (const match of matches) {
      const classified = this.classifyHitItem(match.item);
      if (!classified) continue;
      if (!topmost) {
        topmost = classified.result;
        topmostOwnerLine = classified.ownerLineId;
        topmostOwnerGroup = classified.ownerGroupId;
      }
      // The bias only applies when there's a real choice between
      // construction-related hits. If the topmost result has no
      // owning construction id (it's a piece / decoration / hole
      // body), let it win without scanning deeper.
      if (!topmostOwnerLine && !topmostOwnerGroup) {
        return topmost;
      }
      const lineSelected =
        classified.ownerLineId !== null &&
        preferIds.has(classified.ownerLineId);
      const groupSelected =
        classified.ownerGroupId !== null &&
        preferGroupIds.has(classified.ownerGroupId);
      if (lineSelected || groupSelected) {
        return classified.result;
      }
    }
    return topmost ?? {};
  }

  /** Walk an item's ancestor chain and turn the first kind-tagged
   *  item into the structured hit result returned by `hitTest`,
   *  alongside the owning construction line / group id used by the
   *  selection bias. The owner ids are reported separately from the
   *  public result so e.g. a `line-rotation-handle` doesn't leak a
   *  `constructionId` into branches that distinguish "click on a
   *  line body" from "click on a rotation knob". */
  private classifyHitItem(start: paper.Item | null): {
    result: HitTestResult;
    ownerLineId: string | null;
    ownerGroupId: string | null;
  } | null {
    let item: paper.Item | null = start;
    while (item) {
      if (item.data?.kind === "line-rotation-handle") {
        return {
          result: { lineRotationHandleId: item.data.constructionId },
          ownerLineId: item.data.constructionId,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "decoration") {
        return {
          result: {
            pieceId: item.data.pieceId,
            decorationId: item.data.decorationId,
          },
          ownerLineId: null,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "hole") {
        return {
          result: { pieceId: item.data.pieceId, holeId: item.data.holeId },
          ownerLineId: null,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "cutout") {
        return {
          result: { pieceId: item.data.pieceId, cutoutId: item.data.cutoutId },
          ownerLineId: null,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "stitch") {
        return {
          result: { pieceId: item.data.pieceId, stitchId: item.data.stitchId },
          ownerLineId: null,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "bezier-handle") {
        return {
          result: {
            constructionId: item.data.constructionId,
            bezierHandle: {
              pathIndex: item.data.pathIndex,
              segmentIndex: item.data.segmentIndex,
              side: item.data.side,
            },
          },
          ownerLineId: item.data.constructionId,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "construction-handle") {
        return {
          result: {
            constructionId: item.data.constructionId,
            constructionHandle: {
              pathIndex: item.data.pathIndex,
              segmentIndex: item.data.segmentIndex,
            },
          },
          ownerLineId: item.data.constructionId,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "construction") {
        return {
          result: { constructionId: item.data.constructionId },
          ownerLineId: item.data.constructionId,
          ownerGroupId: null,
        };
      }
      if (item.data?.kind === "construction-group-bbox") {
        return {
          result: { constructionGroupId: item.data.constructionGroupId },
          ownerLineId: null,
          ownerGroupId: item.data.constructionGroupId,
        };
      }
      if (item.data?.kind === "piece") {
        return {
          result: { pieceId: item.data.pieceId },
          ownerLineId: null,
          ownerGroupId: null,
        };
      }
      item = item.parent;
    }
    return null;
  }

  /** Construction-line id set used by the hit-test bias: every line
   *  id in `selection.constructionIds` plus every descendant line id
   *  of every selected group. */
  private selectedConstructionLineIdSet(): Set<string> {
    const sel = this.selection;
    const ids = new Set<string>();
    if (sel.constructionIds && sel.constructionIds.length > 0) {
      for (const id of sel.constructionIds) ids.add(id);
    } else if (sel.constructionId) {
      ids.add(sel.constructionId);
    }
    if (this.project) {
      for (const gid of sel.constructionGroupIds ?? []) {
        for (const lid of descendantConstructionLineIds(this.project, gid)) {
          ids.add(lid);
        }
      }
    }
    return ids;
  }

  private selectedConstructionGroupIdSet(): Set<string> {
    return new Set(this.selection.constructionGroupIds ?? []);
  }

  /** Signature of the global render state an *unselected* piece's group depends
   *  on (#579): edit mode, the handle-visibility toggles renderPiece branches
   *  on, and a probe of the theme colors. When it changes, the whole piece
   *  cache is dropped so no reused group keeps stale styling. (Hover / selection
   *  only affect the selected piece, which is never cached.) */
  private computePieceGlobalSig(): string {
    const colors = [
      "--leather-edge",
      "--leather-border",
      "--leather-fill",
      "--piece-cut",
      "--canvas-accent",
      "--construction-stroke",
      "--construction-halo",
    ]
      .map((v) => cssVar(v))
      .join("|");
    return JSON.stringify({
      editMode: this.editMode,
      showConstructionHandles: this.showConstructionHandles,
      showConstructionRotationHandles: this.showConstructionRotationHandles,
      constructionActive: this.constructionActive,
      partLayoutView: this.partLayoutView,
      colors,
    });
  }

  render(project: Project) {
    // A full render supersedes any in-flight Spread/Assembled slide — it rebuilds
    // the piece groups at the current `partLayoutView`, so stop tweening the old
    // (now-detached) groups (#652).
    this.cancelPartLayoutAnim();
    // Project replacement (open file / reset / reload restore) gets a fresh
    // project id. Re-seed the cached mat layout from the incoming project's
    // persisted placement (#785 follow-up) so a reload restores the exact mat
    // the user was working on — parts keep their spot on it instead of the mat
    // re-centring on their bounding box. When the project carries none (older
    // file, empty project), start null so the next render re-fits from scratch.
    if (this.project?.id !== project.id) {
      this.matRenderer.resetForProject(project.matLayout);
      this.pieceGroupCache.clear();
    }
    this.project = project;
    this.scope.activate();
    this.rootLayer.removeChildren();

    if (this.matVisible) this.matRenderer.renderMat(project);

    // Tracing images (issue #489) sit directly above the mat and below
    // every drawable element — they're references to draw over, never
    // content that may cover the work.
    const tracingImages = projectTracingImages(project);
    // Evict cached bitmaps whose content signature is no longer
    // referenced by any image (deleted, or its source changed), and
    // drop per-image transients for images that are gone.
    const liveSigs = new Set(
      tracingImages.map((img) => this.tracingImageContentSig(img)),
    );
    for (const sig of [...this.tracingImageCanvasByContent.keys()]) {
      if (!liveSigs.has(sig)) this.tracingImageCanvasByContent.delete(sig);
    }
    const liveIds = new Set(tracingImages.map((img) => img.id));
    for (const id of [...this.tracingImagePreview.keys()]) {
      if (!liveIds.has(id)) this.tracingImagePreview.delete(id);
    }
    for (const image of tracingImages) {
      if (!isVisible(image)) continue;
      this.renderTracingImage(image);
    }

    // Incremental piece rendering (#579): an unselected, unchanged piece reuses
    // its previously-built group instead of rebuilding it, so editing one piece
    // in a large project no longer rebuilds them all. Selected pieces always
    // rebuild — their styling tracks live selection / hover, and they're the
    // ones tools transiently drag, so a reused group could never go stale.
    const pieceGlobalSig = this.computePieceGlobalSig();
    if (pieceGlobalSig !== this.pieceGlobalSig) {
      this.pieceGroupCache.clear();
      this.pieceGlobalSig = pieceGlobalSig;
    }
    const pieceSel = this.selection;
    const selectedPieceIds = new Set<string>(pieceSel.pieceIds ?? []);
    if (pieceSel.pieceId) selectedPieceIds.add(pieceSel.pieceId);
    const renderedPieceIds = new Set<string>();
    for (const piece0 of project.pieces) {
      if (!isVisible(piece0)) continue;
      // Live transform preview (#971): while a rotate/resize drag is in flight,
      // render the fully-transformed model piece in place of the committed one,
      // so its holes, stitch seams, cutouts, and decorations all move together
      // — not just the outline. The preview piece comes from the SAME model
      // transform fns the commit uses, so preview and result can't drift.
      const piece =
        this.previewPieceOverride?.id === piece0.id
          ? this.previewPieceOverride.piece
          : piece0;
      renderedPieceIds.add(piece.id);
      if (
        selectedPieceIds.has(piece.id) ||
        this.previewPieceOverride?.id === piece.id
      ) {
        this.pieceGroupCache.delete(piece.id);
        this.pieceRenderer.renderPiece(piece);
        continue;
      }
      const cached = this.pieceGroupCache.get(piece.id);
      if (cached && cached.piece === piece) {
        this.rootLayer.addChild(cached.group); // reattach the detached group
      } else {
        const group = this.pieceRenderer.renderPiece(piece);
        this.pieceGroupCache.set(piece.id, { piece, group });
      }
    }
    // Drop cache entries for pieces no longer rendered (deleted / hidden).
    for (const id of [...this.pieceGroupCache.keys()]) {
      if (!renderedPieceIds.has(id)) this.pieceGroupCache.delete(id);
    }

    // Construction (guide) lines render last — at the project root, not
    // anchored to any piece — so they sit on top of every part and stay
    // visible regardless of part visibility. Style is intentionally
    // subtle (thin dashed slate-blue) so they don't compete with the
    // cut-pattern features underneath.
    const visibleConstruction = projectConstructionLines(project).filter((l) =>
      isVisible(l),
    );
    if (visibleConstruction.length > 0) {
      const conGroup = new this.scope.Group();
      conGroup.data = { kind: "construction-group" };
      // Multi-select: every id in `constructionIds` renders in the
      // selected style, so a group selection (or a shift-click set)
      // highlights consistently. Falls back to the legacy single id
      // when the array is missing — keeps older test scaffolding /
      // direct setters working.
      const selectedSet = new Set(
        this.selection.constructionIds &&
          this.selection.constructionIds.length > 0
          ? this.selection.constructionIds
          : this.selection.constructionId
            ? [this.selection.constructionId]
            : [],
      );
      for (const line of visibleConstruction) {
        const isSel = selectedSet.has(line.id);
        // Sharp-corner hairlines — drawn beneath the yellow stroke
        // so the user keeps a faint visual cue of where each corner
        // *used to be* before the Round sub-tool inscribed an arc
        // (issue #100). Only present when the line carries rounding
        // metadata; un-rounded lines skip this entirely.
        const hairline = this.buildSharpCornerHairline(line);
        if (hairline) conGroup.addChild(hairline);
        // Halo next → drawn under the yellow stroke. The halo is
        // ephemeral so the yellow path on top remains the hit target.
        const halo = this.constructionOverlayRenderer.buildConstructionLineHalo(
          line,
          isSel,
        );
        if (halo) conGroup.addChild(halo);
        // Trimmed halo display (issue #326) — sits between the
        // strokeless main halo and the trimmed yellow stroke so the
        // halo outline survives the per-anchor gaps.
        const haloDisplay =
          this.constructionOverlayRenderer.buildConstructionLineHaloDisplay(
            line,
            isSel,
          );
        if (haloDisplay) conGroup.addChild(haloDisplay);
        const item = this.constructionOverlayRenderer.buildConstructionLineItem(
          line,
          isSel,
        );
        if (item) conGroup.addChild(item);
        // Trimmed yellow stroke (issue #326) — only when the line's
        // handles will be drawn on top. The main item's stroke went
        // null in that branch, so this is the only visible yellow
        // stroke for the line in Edit mode.
        const displayStroke =
          this.constructionOverlayRenderer.buildConstructionLineDisplayStroke(
            line,
            isSel,
          );
        if (displayStroke) conGroup.addChild(displayStroke);
      }
      // Edit handles — small grabbable dots at every anchor point of
      // every construction line. Drawn after the lines so they sit on
      // top and stay clickable. Only shown while the Construction tool
      // is the active tool.
      //
      // Derived (offset) lines never get handles: their geometry is
      // owned by a source line and direct edits are refused
      // everywhere, so a hovering handle dot would be misleading
      // (issue #29 follow-up).
      if (this.showConstructionHandles) {
        for (const line of visibleConstruction) {
          if (line.offsetSource) continue;
          const handles =
            this.constructionOverlayRenderer.buildConstructionHandles(line);
          if (handles) conGroup.addChild(handles);
        }
      }
      // Corner-rounding handles — small inside-the-corner circles
      // that the Round sub-mode picks up as drag targets (issue
      // #100). Same offset-line exclusion: derived geometry isn't
      // editable directly.
      if (this.showCornerHandles) {
        for (const line of visibleConstruction) {
          if (line.offsetSource) continue;
          const handles =
            this.constructionOverlayRenderer.buildCornerHandles(line);
          if (handles) conGroup.addChild(handles);
        }
      }
      // Per-line rotation overlay (issue #67): when the select sub-
      // mode is active and exactly ONE construction line is the
      // active selection (no group, no multi-select), render the
      // tilted bbox + top handle for that line. The corner arc /
      // arrows render for whichever line the user is hovering — any
      // visible non-offset line, not just the selected one — so the
      // corner rotation gesture is discoverable on every shape.
      if (this.showConstructionRotationHandles) {
        const targetId = this.singleSelectedConstructionLineId();
        if (targetId) {
          const line = visibleConstruction.find((l) => l.id === targetId);
          if (line && !line.offsetSource) {
            const overlay =
              this.constructionOverlayRenderer.buildLineRotationOverlay(line);
            if (overlay) conGroup.addChild(overlay);
            // Always-visible rotation pivot crosshair at the effective
            // pivot (issue #420). Axis-aligned sibling of the rotated
            // overlay — the `+` marks a world-space point and should
            // not tilt with the shape. Suppressed while a transient
            // drag crosshair is live to avoid double-drawing.
            if (!this.transientTransform.hasActivePivotCrosshair()) {
              const pivot = effectiveLinePivot(line, this.scope);
              if (pivot) {
                conGroup.addChild(
                  this.buildPersistentPivotCrosshair(pivot, {
                    kind: "line",
                    id: line.id,
                  }),
                );
              }
            }
          }
        }
        const hover = this.lineRotationHoverCorner;
        if (hover && hover.constructionId !== targetId) {
          const hoverLine = visibleConstruction.find(
            (l) => l.id === hover.constructionId,
          );
          if (hoverLine && !hoverLine.offsetSource) {
            const arcGroup = this.buildLineRotationHoverArc(hoverLine);
            if (arcGroup) conGroup.addChild(arcGroup);
          }
        }
      }
      // Group-selection overlay: built AFTER the lines so the union-
      // bounds lookup (via `getConstructionLineItem`) can read the
      // freshly rendered items, then inserted at index 0 so it sits
      // BELOW everything else in z-order. The visual remains visible
      // (the lines are dashed thin and don't fully occlude the
      // frame's dashed strokes); the z-order matters for hit-testing
      // — a click that lands on both a member line and the bbox
      // frame should hit the line first, letting the drill-aware
      // select handler resolve the click as a member click. Clicks
      // on empty patches of the frame still hit the bbox (only the
      // overlay is at that point), preserving the bbox-grab path.
      const selectedGroupIds = this.selection.constructionGroupIds ?? [];
      for (const gid of selectedGroupIds) {
        const overlay =
          this.constructionOverlayRenderer.buildConstructionGroupOverlay(gid);
        if (overlay) conGroup.insertChild(0, overlay);
      }
      // Always-visible rotation pivot crosshair for a single selected
      // group (issue #420) — same lifecycle as the per-line marker.
      if (
        this.showConstructionRotationHandles &&
        !this.transientTransform.hasActivePivotCrosshair() &&
        selectedGroupIds.length === 1
      ) {
        const gid = selectedGroupIds[0]!;
        const group = (project.constructionGroups ?? []).find(
          (g) => g.id === gid,
        );
        if (group) {
          const pivot = effectiveGroupPivot(project, group, this.scope);
          if (pivot) {
            conGroup.addChild(
              this.buildPersistentPivotCrosshair(pivot, {
                kind: "group",
                id: gid,
              }),
            );
          }
        }
      }
    }
    // Re-create the panel-driven hover overlay (if any) on top of
    // the freshly-rendered construction lines — `removeChildren`
    // above wiped the previous overlay, but the hover spec is held
    // separately on the hover controller, so we can rebuild it from
    // the new items.
    this.constructionHover.refreshConstructionHover();
    this.constructionHover.refreshConstructionDeleteHover();
    this.constructionHover.refreshConstructionPanelHover();
    this.constructionHover.refreshConstructionConstraintHover();
    this.constructionHover.refreshConstructionCrosshairs();
    this.constructionHover.refreshConstructionSnapCandidate();
    this.constructionHover.refreshOffsetHoverAffordance();
    this.constructionHover.refreshBezierToggleAffordance();
    this.refreshMeasurement();
    this.refreshDrawSnapPoint();
    // Faint dots on every snappable point (#367) — under the edit handles and
    // the live snap indicator, so a real affordance always wins the pixel.
    this.appendSnapCandidateDots(project);
    // Outline edit handles (#969 pieces, #970 cutouts + stitch lines) —
    // rebuilt on every render so they track the committed geometry;
    // `refreshOutlineEditHandles` keeps them in lock-step mid-drag.
    this.appendOutlineEditHandles();
    // On-canvas rotate/resize handles for the selected leather object (#971).
    this.appendTransformHandles();
    // Written notes (#157) last of all — they are the maker annotating the
    // work, so they sit above the pattern and the guides rather than competing
    // with them for the pixel.
    this.appendNotes(project);
  }

  /**
   * Draw the project's notes (#157).
   *
   * Anchors are resolved here, once per render, because resolving one needs
   * the project (to find the part it points at) while the renderer itself only
   * needs a point — which is what lets the exporter reuse the same drawing code
   * against its own offscreen scope.
   */
  private appendNotes(project: Project): void {
    const notes = numberedNotes(project);
    if (notes.length === 0) return;
    const group = new this.scope.Group();
    group.data = { kind: "notes-group" };
    const selected = new Set(this.selection.noteIds ?? []);
    for (const { note, number } of notes) {
      if (!isNoteVisible(note)) continue;
      const anchors = resolveNoteAnchors(note, project, this.scope).map(
        (a) => a.point,
      );
      group.addChild(
        buildNoteGroup(note, number, anchors, this.scope, {
          selected: selected.has(note.id),
          // Canvas only: the grip invites a drag, and there is nothing to drag
          // on a printed sheet (#1074).
          showResizeGrip: true,
        }),
      );
    }
    this.rootLayer.addChild(group);
  }

  /**
   * Show or hide the faint snap-candidate dots (#367).
   *
   * Snap targets are hard to find, and harder to connect to the toggle that
   * would catch them. Marking every candidate turns "will this snap?" into
   * something you can see before committing to a drag.
   */
  setSnapCandidateKinds(kinds: ActiveSnapKinds | null): void {
    const next = kinds ?? { point: false, line: false };
    if (
      this.snapCandidateKinds.point === next.point &&
      this.snapCandidateKinds.line === next.line
    )
      return;
    this.snapCandidateKinds = next;
    if (this.project) this.render(this.project);
  }

  isSnapCandidatesVisible(): boolean {
    return this.snapCandidateKinds.point || this.snapCandidateKinds.line;
  }

  /** Which tiers are currently being marked — lets a test tell "the Points
   *  chip is off" apart from "the whole feature is off". */
  getSnapCandidateKinds(): ActiveSnapKinds {
    return { ...this.snapCandidateKinds };
  }

  /** The points currently marked as snappable — the e2e suite reads this
   *  rather than scraping Paper items. */
  getSnapCandidatePoints(): { x: number; y: number }[] {
    if (!this.isSnapCandidatesVisible() || !this.project) return [];
    return this.snapCandidatePoints(this.project);
  }

  /**
   * The project the dots should be gathered FROM (#367 follow-up).
   *
   * A gesture doesn't touch the model until release, so gathering from the
   * committed project leaves the dots parked at where the object USED to be
   * while it visibly moves out from under them — the one moment the marks are
   * least believable. Both in-flight channels are honoured instead:
   * `snapCandidateLiveProject`, which a move/drag tool pushes, and the
   * transform gesture's `previewPieceOverride`, which already carries the
   * rotated / resized piece for rendering. Both come from the same model fns
   * the commit runs, so the dots sit where release will actually put them.
   *
   * Returns the committed project unchanged when nothing is in flight — which
   * also keeps the cache hitting, since identity is preserved.
   */
  private candidateSourceProject(project: Project): Project {
    if (this.snapCandidateLiveProject) return this.snapCandidateLiveProject;
    const preview = this.previewPieceOverride;
    if (!preview) return project;
    return {
      ...project,
      pieces: project.pieces.map((p) =>
        p.id === preview.id ? preview.piece : p,
      ),
    };
  }

  private snapCandidatePoints(project: Project): { x: number; y: number }[] {
    const source = this.candidateSourceProject(project);
    const view = this.partLayoutView;
    const kinds = this.snapCandidateKinds;
    const cached = this.snapCandidateCache;
    if (
      cached &&
      cached.project === source &&
      cached.view === view &&
      cached.kinds.point === kinds.point &&
      cached.kinds.line === kinds.line
    ) {
      return cached.points;
    }
    const points = gatherSnapCandidatePoints(source, this.scope, view, kinds);
    this.snapCandidateCache = {
      project: source,
      view,
      kinds: { ...kinds },
      points,
    };
    return points;
  }

  /**
   * Push the in-flight project for a drag that hasn't committed yet, so the
   * dots track the geometry as it moves; `null` on release / cancel puts them
   * back on the committed model. No-op work when the dots are off — callers
   * check `isSnapCandidatesVisible()` first so a drag costs nothing in the
   * default (dots hidden) case.
   */
  setSnapCandidateLiveProject(project: Project | null): void {
    if (this.snapCandidateLiveProject === project) return;
    this.snapCandidateLiveProject = project;
    if (!this.isSnapCandidatesVisible()) return;
    // Only the dots move, so redraw just them: a full re-render mid-drag would
    // fight the tools that mutate their paper items in place.
    this.refreshSnapCandidateDots();
  }

  /** Rebuild just the dot layer, for a mid-drag update. */
  private refreshSnapCandidateDots(): void {
    if (this.snapCandidateDotsItem) {
      this.snapCandidateDotsItem.remove();
      this.snapCandidateDotsItem = null;
    }
    if (this.project) this.appendSnapCandidateDots(this.project);
  }

  private appendSnapCandidateDots(project: Project): void {
    if (!this.isSnapCandidatesVisible()) return;
    const points = this.snapCandidatePoints(project);
    if (points.length === 0) return;
    const zoom = this.scope.view?.zoom ?? 1;
    // Screen-constant, like every other affordance (#864): a dot is a marker,
    // not a piece of geometry, so it must not grow when the user zooms in.
    const r = SNAP_CANDIDATE_DOT_RADIUS_PX / (zoom > 0 ? zoom : 1);
    const ink = cssVar("--canvas-snap-candidate");
    const fill = new this.scope.Color(ink);
    fill.alpha = SNAP_CANDIDATE_DOT_ALPHA;
    const group = new this.scope.Group();
    for (const p of points) {
      const dot = new this.scope.Path.Circle(new this.scope.Point(p.x, p.y), r);
      dot.fillColor = fill;
      dot.strokeColor = new this.scope.Color(ink);
      dot.strokeWidth = SNAP_CANDIDATE_DOT_STROKE_PX;
      // The stroke is an outline, not geometry — keep it one px at every zoom.
      dot.strokeScaling = false;
      group.addChild(dot);
    }
    // Ephemeral so paper's hit-test skips the dots entirely — they're a hint,
    // never a target; the snap itself is a geometric check in `resolveDrawSnap`.
    group.data = { kind: "snap-candidates", ephemeral: true };
    this.rootLayer.addChild(group);
    this.snapCandidateDotsItem = group;
  }

  /** Re-render from the current committed project — used by the leather
   *  TransformController to reset a live preview it mutated in place after a
   *  no-op / cancelled drag. */
  rerender(): void {
    if (this.project) this.render(this.project);
  }

  // ---- Leather transform handles (#971) --------------------------------

  /** True while the Select tool is active — the on-canvas rotate/resize handles
   *  then show for a single selected leather object, with the KIND derived from
   *  the live selection at render time (so handles follow selection changes with
   *  no extra wiring). */
  private transformOnSelect = false;
  /** Active-drag crosshair override: while a rotate/resize/pivot drag is in
   *  flight, the rotation crosshair sits at this explicit point (the fixed orbit
   *  point of a rotate, the parked pivot of a pivot-drag) rather than the
   *  object's derived visual centre. The dashed FRAME itself is always derived
   *  from the (live-preview) model — see `appendTransformHandles` — so it hugs
   *  the geometry at every frame. Null at rest. */
  private transformPreview: { crosshair: { x: number; y: number } } | null =
    null;

  /** Where the user has parked the rotation pivot for the currently selected
   *  transform target, DURING a pivot drag only (keyed so a stale value can't
   *  bleed onto another object). The parked pivot itself lives on the model as
   *  `pivotPoint` (#990 follow-up), the same place construction has kept its
   *  own since #420 — this slot only lets the crosshair follow the cursor
   *  before the drag commits, and is cleared on release. */
  private transformPivotOverride: {
    key: string;
    x: number;
    y: number;
  } | null = null;

  /** Live transform-drag geometry: the fully-transformed model piece rendered
   *  in place of the committed one during a rotate/resize drag, so its holes,
   *  stitch, cutouts, and decorations track live (#971). Null when at rest. */
  private previewPieceOverride: { id: string; piece: Piece } | null = null;

  /** The parked rotation pivot for `key`, or null when none is set for it. */
  getTransformPivotOverride(key: string): { x: number; y: number } | null {
    const o = this.transformPivotOverride;
    return o && o.key === key ? { x: o.x, y: o.y } : null;
  }

  /** Set (with a key + world point) or clear (pass null) the parked rotation
   *  pivot. Clearing is a no-op-safe reset used on drag end / selection change. */
  setTransformPivotOverride(key: string | null, x?: number, y?: number): void {
    this.transformPivotOverride =
      key === null || x === undefined || y === undefined ? null : { key, x, y };
  }

  /** Translate the rendered leather transform overlay (dashed frame + rotation
   *  crosshair) by `(dx, dy)` so it tracks a drag-to-move in lock-step instead
   *  of snapping into place only on commit — the same per-frame overlay
   *  translate construction lines do for their halo / rotation frame (#85).
   *  VISUAL ONLY: the parked pivot rides on the model and is carried by the
   *  translate itself on commit (#990 follow-up), so a cancelled drag leaves
   *  it untouched without any bookkeeping here. */
  translateTransformOverlay(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    const delta = new this.scope.Point(dx, dy);
    for (const c of this.rootLayer.children ?? []) {
      const k = c.data?.kind;
      if (
        k === LEATHER_TRANSFORM_OVERLAY_KINDS.overlay ||
        k === "leather-transform-pivot"
      )
        c.translate(delta);
    }
  }

  setTransformOnSelect(on: boolean) {
    if (this.transformOnSelect === on) return;
    this.transformOnSelect = on;
    if (this.project) this.render(this.project);
  }

  /** Which single leather object the selection means — the shared answer to
   *  that question (#1033). Used by the transform overlay, the point-edit
   *  overlay and the tool, so none of them can disagree about what is being
   *  edited. Null for a multi-selection, or a hole / decoration selection
   *  (not an outline). */
  selectionTransformKind(): "piece" | "cutout" | "stitch" | null {
    const sel = this.selection;
    if (!sel.pieceId) return null;
    if ((sel.pieceIds?.length ?? 0) > 1) return null;
    if ((sel.stitchIds?.length ?? 0) > 1) return null;
    if (sel.holeId || sel.decorationId) return null;
    if (sel.cutoutId) return "cutout";
    if (sel.stitchId) return "stitch";
    return "piece";
  }

  /** Overlay-only refresh (dashed frame + pivot crosshair) — a lightweight
   *  remove + re-append, no full render. Used for a pivot drag (geometry
   *  unchanged, only the crosshair moves) and to clear at rest. `crosshair`
   *  parks the rotation crosshair; passing null clears any live preview piece
   *  and reverts the crosshair to the object's derived visual centre. */
  setTransformPreview(crosshair: { x: number; y: number } | null) {
    this.transformPreview = crosshair ? { crosshair } : null;
    if (!crosshair) this.previewPieceOverride = null;
    for (const c of (this.rootLayer.children ?? []).slice()) {
      const k = c.data?.kind;
      if (
        k === LEATHER_TRANSFORM_OVERLAY_KINDS.overlay ||
        k === "leather-transform-pivot"
      )
        c.remove();
    }
    this.appendTransformHandles();
  }

  /** Live drag preview for a rotate/resize gesture on a leather PART: swap in
   *  the fully-transformed model `piece` (holes / stitch / cutouts / decorations
   *  all move) via one full render — so nothing lags. The dashed frame is then
   *  DERIVED from that preview piece (like the at-rest frame is from the
   *  committed model), so it always hugs the geometry, no matter how a tilted
   *  object's rotation composes. `crosshair` parks the rotation crosshair at the
   *  gesture's fixed orbit point. `piece` comes from the same model fns the
   *  commit uses, so the preview is a pixel-exact rehearsal (#971). */
  setTransformDragPreview(crosshair: { x: number; y: number }, piece: Piece) {
    this.transformPreview = { crosshair };
    this.previewPieceOverride = { id: piece.id, piece };
    if (this.project) this.render(this.project);
  }

  /**
   * Live preview for an Edit-points drag (#982): swap in a `piece` carrying the
   * in-flight geometry so the canvas re-derives from it.
   *
   * This is what makes a bezier-handle drag on a SEAM show up. The drag mutates
   * the phantom edit path, which is the item the controller owns — but a seam's
   * visible guide AND its chisel marks are rendered from the MODEL, so without
   * this the dashed helper bends under the cursor while the seam itself and
   * every mark on it sit still until release. The marks are the seam, so that
   * reads as nothing happening.
   *
   * Reuses the transform gesture's override (#971) rather than growing a second
   * preview channel; `clearEditDragPreview` puts the committed model back.
   */
  setEditDragPreview(piece: Piece) {
    this.previewPieceOverride = { id: piece.id, piece };
    if (this.project) this.render(this.project);
  }

  /** Drop the Edit-points live preview and re-render the committed model. */
  clearEditDragPreview() {
    if (!this.previewPieceOverride) return;
    this.previewPieceOverride = null;
    if (this.project) this.render(this.project);
  }

  /** The model object a transform `spec` targets (piece / cutout / stitch).
   *  Reads the LIVE PREVIEW piece while a rotate/resize drag is in flight, so
   *  the overlay frame + rotation frame derive from the same transformed model
   *  the canvas is showing — no separate "preview box" that could drift (#971). */
  private transformModelObject(
    spec: OutlineEditHandlesSpec,
  ): Piece | Cutout | StitchLine | null {
    const preview = this.previewPieceOverride;
    const piece =
      preview && preview.id === spec.pieceId
        ? preview.piece
        : this.project?.pieces.find((p) => p.id === spec.pieceId);
    if (!piece) return null;
    if (spec.kind === "piece") return piece;
    if (spec.kind === "cutout")
      return piece.cutouts.find((c) => c.id === spec.cutoutId) ?? null;
    return pieceStitchLines(piece).find((l) => l.id === spec.stitchId) ?? null;
  }

  /** The pivot the user parked on this object, or null when it still sits at
   *  the default. Read from the MODEL (#990 follow-up) so the crosshair is
   *  drawn where it was left after selecting something else or reloading —
   *  the live drag override, when present, wins over it. */
  private objectPivotFor(spec: OutlineEditHandlesSpec): Vec2 | null {
    return this.transformModelObject(spec)?.pivotPoint ?? null;
  }

  /** The object's persisted baked-rotation frame, or null when un-tilted. */
  private objectRotationFor(
    spec: OutlineEditHandlesSpec,
  ): { deg: number; center: { x: number; y: number } } | null {
    const o = this.transformModelObject(spec);
    return o && o.rotation !== undefined && o.rotationCenter
      ? { deg: o.rotation, center: o.rotationCenter }
      : null;
  }

  /** Public accessor for a transform target's baked-rotation frame — the
   *  TransformController un-rotates the cursor by this (#971). */
  transformRotationFor(
    spec: OutlineEditHandlesSpec,
  ): { deg: number; center: { x: number; y: number } } | null {
    return this.objectRotationFor(spec);
  }

  /** The world-mm **un-rotated** bounding box of a transform target (#971): the
   *  baked tilt is undone before measuring, so a tilted part reports its natural
   *  W×H (the frame the resize handles operate in), not the fat diagonal AABB.
   *  Reads model geometry (a piece/cutout outline, a seam's points). Null when
   *  unmeasurable. */
  transformBoxFor(spec: OutlineEditHandlesSpec): Box | null {
    const obj = this.transformModelObject(spec);
    if (!obj) return null;
    const rot = this.objectRotationFor(spec);
    const unrotate = (p: { x: number; y: number }): { x: number; y: number } =>
      rot
        ? new this.scope.Point(p.x, p.y).rotate(
            -rot.deg,
            new this.scope.Point(rot.center.x, rot.center.y),
          )
        : p;
    if (spec.kind === "stitch") {
      const line = obj as StitchLine;
      if (line.points.length < 2) return null;
      const pts = line.points.map(unrotate);
      const xs = pts.map((p) => p.x);
      const ys = pts.map((p) => p.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
    }
    const d =
      spec.kind === "piece"
        ? (obj as Piece).shapePathData
        : (obj as Cutout).pathData;
    const cp = new this.scope.CompoundPath({ pathData: d, insert: false });
    if (rot)
      cp.rotate(-rot.deg, new this.scope.Point(rot.center.x, rot.center.y));
    const b = cp.bounds;
    const box =
      b.width <= 0 && b.height <= 0
        ? null
        : { x: b.x, y: b.y, width: b.width, height: b.height };
    cp.remove();
    return box;
  }

  private appendTransformHandles(): void {
    if (!this.transformOnSelect) return;
    const kind = this.selectionTransformKind();
    if (!kind) return;
    const spec = this.outlineSpecForKind(kind);
    if (!spec) return;
    const ownerKey = this.specKey(spec);
    // Drop a pivot override left over from a previously selected object.
    if (
      this.transformPivotOverride &&
      this.transformPivotOverride.key !== ownerKey
    )
      this.transformPivotOverride = null;
    // Frame geometry is ALWAYS derived from the model — the live preview piece
    // during a drag (via transformModelObject), the committed geometry at rest —
    // so the dashed frame hugs the object every frame, however its rotation
    // composes. `center` tilts the frame about the object's baked rotation
    // centre; the un-rotated box is drawn and rotated by `rotationDeg` about it.
    const box = this.transformBoxFor(spec);
    if (!box) return;
    const rot = this.objectRotationFor(spec);
    const rotationDeg = rot?.deg ?? 0;
    const boxCenter = {
      x: box.x + box.width / 2,
      y: box.y + box.height / 2,
    };
    const center = rot?.center ?? boxCenter;
    // The rotation crosshair sits at the object's DISPLAYED centre by default —
    // NOT the baked rotationCenter, which for a cutout / seam that inherited its
    // tilt from the part is the PART's centre, far away. Same shared rule the
    // controller's `effectivePivot` and construction's #420 pivot use (#990).
    // An active drag pins it to the gesture's orbit point; a parked pivot wins.
    const pivot =
      this.transformPreview?.crosshair ??
      this.getTransformPivotOverride(ownerKey) ??
      this.objectPivotFor(spec) ??
      displayedBoxCenter(box, rot);
    const overlay = this.constructionOverlayRenderer.buildTransformOverlay({
      box,
      center,
      rotationDeg,
      // Seams scale uniformly only (no single-dimension edge handles).
      allowEdge: kind !== "stitch",
      stroke: LEATHER_SELECTION_STROKE(),
      kinds: LEATHER_TRANSFORM_OVERLAY_KINDS,
      data: { transformOwner: ownerKey },
      hoverArc: null,
    });
    // Everything above is in the part's own space, like the TransformController
    // that works these handles; draw it where the part is drawn (#1140).
    const off = this.specDisplayOffset(spec);
    if (overlay) {
      overlay.translate(new this.scope.Point(off.x, off.y));
      this.rootLayer.addChild(overlay);
    }
    // A small pivot crosshair so the rotation centre is visible + grabbable.
    this.appendTransformPivotCrosshair({
      x: pivot.x + off.x,
      y: pivot.y + off.y,
    });
  }

  private appendTransformPivotCrosshair(pivot: { x: number; y: number }): void {
    const zoom = this.scope.view?.zoom ?? 1;
    const r = 2.4 / zoom;
    const cross = new this.scope.Group();
    for (const [dx, dy] of [
      [
        [-r, 0],
        [r, 0],
      ],
      [
        [0, -r],
        [0, r],
      ],
    ] as const) {
      const seg = new this.scope.Path.Line(
        new this.scope.Point(pivot.x + dx[0], pivot.y + dx[1]),
        new this.scope.Point(pivot.x + dy[0], pivot.y + dy[1]),
      );
      seg.strokeColor = new this.scope.Color(LEATHER_SELECTION_STROKE());
      seg.strokeWidth = 1;
      seg.strokeScaling = false;
      cross.addChild(seg);
    }
    cross.data = { kind: "leather-transform-pivot", ephemeral: true };
    this.rootLayer.addChild(cross);
  }

  /** Where `spec`'s part is drawn relative to its model geometry: a linked
   *  part moved in the Spread view is shifted by its cut-layout offset, and so
   *  is everything inside it (#1140). The transform frame and the Edit-points
   *  phantom are built from the model, so they add this to land on the object
   *  as drawn. */
  private specDisplayOffset(spec: OutlineEditHandlesSpec): {
    x: number;
    y: number;
  } {
    const piece = this.project?.pieces.find((p) => p.id === spec.pieceId);
    if (!piece) return { x: 0, y: 0 };
    const { dx, dy } = pieceDisplayOffset(piece, this.partLayoutView);
    return { x: dx, y: dy };
  }

  private specKey(spec: OutlineEditHandlesSpec): string {
    if (spec.kind === "piece") return `piece:${spec.pieceId}`;
    if (spec.kind === "cutout")
      return `cutout:${spec.pieceId}:${spec.cutoutId}`;
    return `stitch:${spec.pieceId}:${spec.stitchId}`;
  }

  /** The outline spec for a KIND against the live selection — shared by the
   *  point-edit overlay and the transform overlay. Returns null for
   *  construction-linked (re-derived) / cropped objects (no handles). */
  private outlineSpecForKind(
    kind: OutlineEditHandlesSpec["kind"],
  ): OutlineEditHandlesSpec | null {
    if (!this.project) return null;
    const sel = this.selection;
    const piece = this.project.pieces.find((p) => p.id === sel.pieceId);
    if (!piece) return null;
    if (kind === "piece") {
      if (piece.constructionOutline) return null;
      return { kind, pieceId: piece.id };
    }
    if (kind === "cutout") {
      const cutout = piece.cutouts.find((c) => c.id === sel.cutoutId);
      if (!cutout || cutout.constructionOutline) return null;
      return { kind, pieceId: piece.id, cutoutId: cutout.id };
    }
    const line = pieceStitchLines(piece).find((l) => l.id === sel.stitchId);
    if (!line || line.constructionOutline || line.authoredPoints) return null;
    return { kind, pieceId: piece.id, stitchId: line.id };
  }

  /** Whether the leather Edit-points tool is active (#969 / #970). The
   *  target derives from the live selection at render time, so handles
   *  follow selection changes (incl. programmatic ones) with no extra
   *  wiring — and since #1033 the KIND does too, so the one tool edits
   *  whichever of part / cut-out / seam is selected rather than being
   *  told which of the three to look at. */
  private outlineEditActive = false;

  setOutlineEditActive(active: boolean) {
    if (this.outlineEditActive === active) return;
    this.outlineEditActive = active;
    if (this.project) this.render(this.project);
  }

  /** The spec the point-edit overlay is currently editing. Public because
   *  the tool needs the SAME answer the overlay draws — two copies of
   *  "which object does this selection mean" is how a tool ends up
   *  editing one object while the handles sit on another. Derived
   *  (construction-linked) and cropped targets get no handles; the tool
   *  refuses their edits anyway. */
  outlineEditSpec(): OutlineEditHandlesSpec | null {
    const kind = this.selectionTransformKind();
    if (!kind) return null;
    return this.outlineSpecForKind(kind);
  }

  private currentOutlineEditSpec(): OutlineEditHandlesSpec | null {
    return this.outlineEditActive ? this.outlineEditSpec() : null;
  }

  /** The rendered outline path of a piece (the structural-mode child
   *  tagged `role: "shape"`) — the item the outline-edit drags mutate
   *  per-frame. Null when the piece isn't rendered (or display mode
   *  replaced the raw outline with the clipped leather composite). */
  getPieceShapeItem(pieceId: string): paper.Item | null {
    const group = this.getPieceGroup(pieceId);
    if (!group || !group.children) return null;
    for (const child of group.children) {
      if (child.data?.kind === "piece" && child.data.role === "shape") {
        return child;
      }
    }
    return null;
  }

  /** The live paper item the outline-edit drags mutate for `spec`:
   *  the piece's structural outline, the cutout's rendered path, or —
   *  for a stitch line — the PHANTOM edit path this overlay draws
   *  (the seam renders as holes + a guide, so the editable polyline
   *  is the overlay's own path; holes re-derive on commit). */
  getOutlineEditItem(spec: OutlineEditHandlesSpec): paper.Item | null {
    if (spec.kind === "piece") return this.getPieceShapeItem(spec.pieceId);
    if (spec.kind === "cutout")
      return this.getCutoutItem(spec.pieceId, spec.cutoutId);
    for (const child of this.rootLayer.children ?? []) {
      if (
        child.data?.kind === "stitch-edit-path" &&
        child.data.stitchId === spec.stitchId
      ) {
        return child;
      }
    }
    return null;
  }

  /** Re-paint the outline edit-handle dots from the LIVE item mid-drag
   *  — the leather analogue of `refreshConstructionHandles`. The
   *  stitch phantom path is NOT rebuilt here (the drag is mutating
   *  it); only the handle dots re-derive from it. */
  refreshOutlineEditHandles() {
    const spec = this.currentOutlineEditSpec();
    if (!spec) return;
    for (const child of (this.rootLayer.children ?? []).slice()) {
      if (child.data?.kind === "outline-edit-handles") child.remove();
    }
    const item = this.getOutlineEditItem(spec);
    if (item) this.appendOutlineEditHandleDots(item);
  }

  /** Build + append the edit overlay for `outlineEditHandlesFor` from
   *  the live rendered outline: for stitch targets first the phantom
   *  polyline path (the editable geometry), then the handle dots.
   *  Reuses the construction handle renderer so the edit modes read
   *  identically; every group is marked ephemeral so `hitTest` passes
   *  through (the edit tool does its own distance-based testing). */
  private appendOutlineEditHandles(): void {
    const spec = this.currentOutlineEditSpec();
    if (!spec) return;
    if (spec.kind === "stitch") {
      // Read the LIVE PREVIEW piece when a drag is in flight (#982). A render
      // clears the overlay and rebuilds the phantom from scratch, so taking the
      // committed model here rebuilt it STRAIGHT on every frame and wiped the
      // bend in progress — the drag looked dead and committed nothing. Same
      // rule `transformModelObject` follows for the transform gesture.
      const previewPiece =
        this.previewPieceOverride?.id === spec.pieceId
          ? this.previewPieceOverride.piece
          : null;
      const piece =
        previewPiece ?? this.project?.pieces.find((p) => p.id === spec.pieceId);
      const line = piece
        ? pieceStitchLines(piece).find((l) => l.id === spec.stitchId)
        : undefined;
      if (!line || line.points.length < 2) return;
      // The seam's REAL geometry, curves included (#982). This path is the live
      // item every edit drag mutates and every commit reads back, so building it
      // as a polyline from `points` silently discarded the seam's existing
      // handles: bending one run committed a path where only that run was
      // curved, and every other curve was lost. Construction never hits this
      // because its live item IS its stored `pathData`; going through
      // `seamPathData` makes the seam round-trip the same way.
      const phantom = new this.scope.Path(seamPathData(line));
      // Drawn where the seam is drawn: on a part moved in the Spread view,
      // shifted by its cut-layout offset. The Edit-points tool works in this
      // displayed space and maps back to the model on commit (#1140).
      const off = this.specDisplayOffset(spec);
      phantom.translate(new this.scope.Point(off.x, off.y));
      phantom.strokeColor = new this.scope.Color(SELECTION_STROKE());
      phantom.strokeWidth = 1;
      phantom.strokeScaling = false;
      phantom.dashArray = [2, 2];
      phantom.fillColor = null;
      phantom.data = {
        kind: "stitch-edit-path",
        pieceId: spec.pieceId,
        stitchId: spec.stitchId,
        ephemeral: true,
      };
      this.rootLayer.addChild(phantom);
    }
    const item = this.getOutlineEditItem(spec);
    if (item) this.appendOutlineEditHandleDots(item);
  }

  private appendOutlineEditHandleDots(item: paper.Item): void {
    // Locked anchors render with the same styling construction uses — the lock
    // map reads the target model's own smoothAnchors (#969 parity). Seams
    // carry locks too since they gained curves (#982).
    const spec = this.currentOutlineEditSpec();
    const piece = spec
      ? this.project?.pieces.find((p) => p.id === spec.pieceId)
      : undefined;
    const lockSource =
      spec?.kind === "piece"
        ? piece
        : spec?.kind === "cutout"
          ? piece?.cutouts.find((c) => c.id === spec.cutoutId)
          : spec?.kind === "stitch" && piece
            ? pieceStitchLines(piece).find((l) => l.id === spec.stitchId)
            : undefined;
    const handles =
      this.constructionOverlayRenderer.buildConstructionHandlesFromPath(
        item as paper.CompoundPath,
        "outline-edit",
        buildAnchorLockMap(lockSource),
        // Leather selection accent — construction's yellow is hard to
        // read against the tan leather background (#970 fix).
        LEATHER_SELECTION_STROKE(),
      );
    if (!handles) return;
    handles.data = { kind: "outline-edit-handles", ephemeral: true };
    this.rootLayer.addChild(handles);
  }

  /** Build a faint dashed overlay tracing the line's *sharp* source
   *  geometry (issue #100). Only present when the line has
   *  `cornerRounding` metadata; un-rounded lines return `null` so
   *  the renderer can cheaply skip the call. Drawn beneath the
   *  primary yellow stroke so the rounded arcs stay the dominant
   *  visual while the user can still see "where the corner was". */
  private buildSharpCornerHairline(line: ConstructionLine): paper.Item | null {
    const rounding = line.cornerRounding;
    if (!rounding || rounding.corners.length === 0) return null;
    let path: paper.PathItem;
    try {
      path = new this.scope.CompoundPath({
        pathData: rounding.sharpPathData,
      });
    } catch {
      return null;
    }
    path.data = {
      kind: "construction-sharp-hairline",
      constructionId: line.id,
    };
    path.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
    path.strokeColor.alpha = 0.35;
    path.strokeWidth = 1;
    path.strokeScaling = false;
    path.dashArray = [1, 2];
    path.fillColor = null;
    return path;
  }

  /** Refresh the handle indicators for one construction line in
   *  place. Reads segments from the live (already-mutated) rendered
   *  item so per-frame curve-bend drags can keep the handle line +
   *  hollow-tip indicators in lock-step with the bending stroke
   *  without committing the model. */
  refreshConstructionHandles(constructionId: string) {
    if (!this.showConstructionHandles) return;
    let conGroup: paper.Item | null = null;
    let lineItem: paper.Item | null = null;
    for (const child of this.rootLayer.children ?? []) {
      if (child.data?.kind !== "construction-group") continue;
      conGroup = child;
      for (const sub of child.children ?? []) {
        if (
          sub.data?.kind === "construction" &&
          sub.data.constructionId === constructionId
        ) {
          lineItem = sub;
        }
      }
    }
    if (!conGroup || !lineItem) return;
    // Remove the previous handles subgroup for this line.
    for (const sub of (conGroup.children ?? []).slice()) {
      if (
        sub.data?.kind === "construction-handles" &&
        sub.data.constructionId === constructionId
      ) {
        sub.remove();
      }
    }
    // Look up the line model so we can render locked anchors with
    // the right styling. Falls back to "no anchors locked" if the
    // line isn't in the project (shouldn't happen mid-drag).
    const line = this.project
      ? projectConstructionLines(this.project).find(
          (l) => l.id === constructionId,
        )
      : undefined;
    const lockMap = buildAnchorLockMap(line);
    const fresh =
      this.constructionOverlayRenderer.buildConstructionHandlesFromPath(
        lineItem as paper.CompoundPath,
        constructionId,
        lockMap,
      );
    if (fresh) conGroup.addChild(fresh);
  }

  /** Draws the green cutting-mat background + grey grid lines beneath all
   *  rendered pieces. Mat size is picked from the smallest standard cutting
   *  mat that envelops the parts' bounding box (with a 20 mm margin); the
   *  mat is centered on that bbox so the cross-axes always sit at the
   *  visual center of the work. All children are flagged ephemeral so
   *  hit-tests pass right through to the leather. */
  /** Place one tracing image (issue #489). Cache hit → a locked raster
   *  sized/positioned in mm; miss → derive the warped canvas in the
   *  background and re-render when it lands. */
  /** Content signature for a tracing image's rendered bitmap (the
   *  things `renderTracingImageCanvas` actually reads). Two images with
   *  the same signature — e.g. a duplicate and its original — share one
   *  decoded/warped canvas. A rectified final copy depends only on its
   *  URL; otherwise the warp depends on the source URL + the crop quad
   *  + the physical size. */
  private tracingImageContentSig(image: TracingImage): string {
    if (image.finalUrl) return `final:${image.finalUrl}`;
    const quad = image.quad
      .map((p) => `${p.x.toFixed(5)},${p.y.toFixed(5)}`)
      .join(";");
    return `warp:${image.url}|${quad}|${image.widthMm}x${image.heightMm}`;
  }

  private renderTracingImage(image: TracingImage) {
    // A full render rebuilds the raster + overlay at the committed
    // rotation, so any in-flight live-rotation delta tracking resets
    // (issue #526).
    this.tracingImageLiveRotation.delete(image.id);
    // Resolve the rendered bitmap by content (#545 follow-up): copies
    // sharing the same source + crop + size reuse one decode/warp
    // instead of each loading the identical resource.
    const sig = this.tracingImageContentSig(image);
    const cached = this.tracingImageCanvasByContent.get(sig);
    // Kick off the derivation the first time this content is seen. Until
    // it lands we draw a placeholder (issue #510) so the image's
    // position is visible immediately instead of popping in.
    if (cached === undefined) {
      this.tracingImageCanvasByContent.set(sig, "loading");
      renderTracingImageCanvas(image)
        .then((canvas) => {
          // Drop the result if the entry was evicted while in flight.
          if (this.tracingImageCanvasByContent.get(sig) !== "loading") return;
          this.tracingImageCanvasByContent.set(sig, canvas);
          if (this.project) this.render(this.project);
        })
        .catch((e) => {
          if (this.tracingImageCanvasByContent.get(sig) !== "loading") return;
          console.warn(`Tracing image "${image.name}" failed to load:`, e);
          this.tracingImageCanvasByContent.set(sig, "error");
        });
    }
    const current = this.tracingImageCanvasByContent.get(sig);
    if (current instanceof HTMLCanvasElement) {
      // The real raster has landed — the transient crop preview
      // (issue #510) is no longer needed for this image.
      this.tracingImagePreview.delete(image.id);
      this.addTracingImageRaster(image, current, false);
    } else {
      // No usable bitmap yet (first load or error) — draw the
      // placeholder so the user sees where the image lands. Shows the
      // cropped preview from the trace dialog (#510) when one is on
      // hand, else a faint framed rectangle.
      this.renderTracingImagePlaceholder(image);
    }
    this.addTracingImageOverlay(image);
  }

  /** Place the warped (or preview/placeholder) raster for a tracing
   *  image at its mm bounds. `locked` keeps it out of Paper's hit-test
   *  so drawing tools draw through it; the Select-sub-mode grab uses
   *  the geometric `hitTestTracingImage` instead. */
  private addTracingImageRaster(
    image: TracingImage,
    canvas: HTMLCanvasElement,
    isPlaceholder: boolean,
  ) {
    const raster = new this.scope.Raster(canvas);
    raster.data = {
      kind: "tracing-image",
      tracingImageId: image.id,
      ...(isPlaceholder ? { placeholder: true } : {}),
    };
    raster.bounds = new this.scope.Rectangle(
      image.x,
      image.y,
      image.widthMm,
      image.heightMm,
    );
    raster.locked = true;
    // User-controlled fade (issue #516); defaults to 0.75. The preview
    // placeholder fades a touch more so it reads as provisional.
    const opacity = tracingImageOpacity(image);
    raster.opacity = isPlaceholder ? opacity * 0.85 : opacity;
    // Flip (issue #545) then rotate, both about the image centre. The
    // mirror is a horizontal reflection in the image's own pre-rotation
    // frame, so it must be applied before the rotation; `.rotate` adds a
    // relative turn (the raster starts unrotated) which composes cleanly
    // on top of the reflected matrix — unlike `.rotation =`, whose getter
    // mis-decomposes a reflected matrix as a 180° turn. This flips the
    // rendered pixels for the warp and stored-final-copy bitmaps alike.
    if (image.mirrored) raster.scale(-1, 1);
    raster.rotate(tracingImageRotation(image));
    this.rootLayer.addChild(raster);
  }

  /** Loading-state placeholder (issue #510): the cropped preview handed
   *  over by the trace dialog when available, otherwise a faint dashed
   *  rectangle at the target bounds so the future position is visible
   *  while the full-resolution warp derives. */
  private renderTracingImagePlaceholder(image: TracingImage) {
    const preview = this.tracingImagePreview.get(image.id);
    if (preview) {
      this.addTracingImageRaster(image, preview, true);
      return;
    }
    const rect = new this.scope.Path.Rectangle(
      new this.scope.Rectangle(image.x, image.y, image.widthMm, image.heightMm),
    );
    const fill = new this.scope.Color(MAT_GRID_MAJOR());
    fill.alpha = 0.18;
    rect.fillColor = fill;
    rect.strokeColor = new this.scope.Color(MAT_GRID_MAJOR());
    rect.strokeWidth = 1;
    rect.strokeScaling = false;
    rect.dashArray = [4, 3];
    rect.locked = true;
    rect.rotation = tracingImageRotation(image);
    rect.data = {
      kind: "tracing-image",
      tracingImageId: image.id,
      placeholder: true,
    };
    this.rootLayer.addChild(rect);
  }

  /** Selection + rotation overlay for a tracing image (issues #509 /
   *  #526) — the same dashed bbox + stem + knob "rotation box" a
   *  construction line gets. Built in the image's un-rotated frame and
   *  rotated as one group about the image centre, so the box always
   *  matches the rotated raster (no double-rotation). The stem + knob
   *  appear only when this image is the sole selection and the Select
   *  sub-mode's rotation handles are on — mirroring the construction
   *  line affordance. Rides the live drag / live rotation via
   *  `translateTracingImageRender` / `setTracingImageRotationLive`. */
  private addTracingImageOverlay(image: TracingImage) {
    if (!(this.selection.tracingImageIds ?? []).includes(image.id)) return;
    const sel = this.selection;
    const soleSelection =
      (sel.tracingImageIds ?? []).length === 1 &&
      (sel.constructionIds ?? []).length === 0 &&
      (sel.constructionGroupIds ?? []).length === 0;
    const withHandle = soleSelection && this.showConstructionRotationHandles;
    const rot = tracingImageRotation(image);
    const center = new this.scope.Point(
      image.x + image.widthMm / 2,
      image.y + image.heightMm / 2,
    );

    const frame = new this.scope.Path.Rectangle({
      rectangle: new this.scope.Rectangle(
        image.x,
        image.y,
        image.widthMm,
        image.heightMm,
      ),
      strokeColor: new this.scope.Color(SELECTION_STROKE()),
      strokeWidth: 1.4,
      strokeScaling: false,
      dashArray: [6, 4],
      fillColor: null,
    });
    frame.data = { kind: "tracing-image-bbox", tracingImageId: image.id };
    const overlay = new this.scope.Group([frame]);
    overlay.data = {
      kind: "tracing-image-overlay",
      tracingImageId: image.id,
      ephemeral: true,
    };

    if (withHandle) {
      // Screen-constant rotation knob + stem (#864): the mm constants
      // are "screen px at zoom 1", divided by the live zoom so the knob
      // holds its on-screen size, matching the line / group overlays and
      // the paired hit-test in rotationController.isOnImageRotationHandle.
      const zoom = this.scope.view?.zoom ?? 1;
      const topX = image.x + image.widthMm / 2;
      const topY = image.y;
      // Screen-constant stem offset: the gap above the bbox is "screen px at
      // zoom 1" divided by the live zoom, matching the line / group overlays
      // and the paired hit-test in rotationController.isOnImageRotationHandle.
      const stemEnd = topY - Scene.LINE_ROTATION_HANDLE_OFFSET_MM / zoom;
      const stem = new this.scope.Path.Line(
        new this.scope.Point(topX, topY),
        new this.scope.Point(topX, stemEnd),
      );
      stem.strokeColor = new this.scope.Color(SELECTION_STROKE());
      stem.strokeWidth = 1;
      stem.strokeScaling = false;
      stem.data = { kind: "tracing-image-rotation-stem", ephemeral: true };
      const knob = new this.scope.Path.Circle(
        new this.scope.Point(topX, stemEnd),
        Scene.LINE_ROTATION_HANDLE_RADIUS_MM / zoom,
      );
      knob.fillColor = new this.scope.Color(SELECTION_STROKE());
      knob.strokeColor = null;
      knob.strokeScaling = false;
      knob.data = {
        kind: "tracing-image-rotation-handle",
        tracingImageId: image.id,
        ephemeral: true,
      };
      overlay.addChild(stem);
      overlay.addChild(knob);
    }

    if (Math.abs(rot) > 1e-6) overlay.rotate(rot, center);
    this.rootLayer.addChild(overlay);

    // Always-visible rotation pivot crosshair (#420) for a sole-selected
    // image — the draggable `+` marking the point the rotation knob spins
    // about. A separate root-level item (NOT in the rotated `overlay`) so
    // it stays axis-aligned at the fixed pivot. Suppressed while a transient
    // / rotation-drag crosshair is live, matching the line / group path.
    if (withHandle && !this.transientTransform.hasActivePivotCrosshair()) {
      const pivot = effectiveImagePivot(image);
      this.rootLayer.addChild(
        this.buildPersistentPivotCrosshair(pivot, {
          kind: "image",
          id: image.id,
        }),
      );
    }
  }

  /** Active grid step sizes (mm). Three tiers driven by the units
   *  preference so a metric mat reads 1 cm / 5 cm / 10 cm and an imperial
   *  mat reads 1/4" / 1" / 6". */
  /** Arrow-key nudge step in project mm (#727), by grid tier: the ruler's
   *  smallest tick (`"tick"` — 1 mm metric / 1/16" imperial), the minor grid
   *  line (`"minor"` — 10 mm / 1/4"), or the major grid line (`"major"` —
   *  50 mm / 1"). Unit-based, returned regardless of whether the mat / grid is
   *  currently shown. */
  nudgeStep(level: "tick" | "minor" | "major"): number {
    if (level === "tick") return this.matUnits === "in" ? 25.4 / 16 : 1;
    const s = this.gridSteps();
    return level === "major" ? s.major : s.minor;
  }

  gridSteps(): { minor: number; major: number; extra: number } {
    if (this.matUnits === "in") {
      return { minor: 25.4 / 4, major: 25.4, extra: 25.4 * 6 };
    }
    return { minor: 10, major: 50, extra: 100 };
  }

  /** True when handle dots will be rendered on this line — same gate
   *  used by the handle render loop. Derived (offset) lines never get
   *  handles. */
  lineHandlesVisible(line: ConstructionLine): boolean {
    return this.showConstructionHandles && !line.offsetSource;
  }

  /** Project-space distance the rotation top-handle hangs above the
   *  bbox top edge for a construction line (issue #67). Mirrors the
   *  value ConstructionTool uses for hit-testing so the rendered knob
   *  matches the geometric pick. */
  static readonly LINE_ROTATION_HANDLE_OFFSET_MM = TRANSFORM_KNOB_OFFSET_MM;
  /** Radius (screen px at the live zoom) of the rotation knob. Matches the
   *  resize-handle half-size so the knob reads as big as the corner squares
   *  (#971 follow-up). Visual only; the grab is a separate geometric check. */
  static readonly LINE_ROTATION_HANDLE_RADIUS_MM = TRANSFORM_KNOB_RADIUS_MM;
  /** Half-side (screen px at the live zoom — rendered as `value / zoom`) of the
   *  square corner + edge resize handles drawn on a selected, unrotated line
   *  (#388, #863). A ~7 px square reads clearly and is comfortable to target.
   *  Visual only; the grab is a geometric check in `resizeHandleAt`. */
  static readonly LINE_RESIZE_HANDLE_HALF_MM = TRANSFORM_RESIZE_HANDLE_HALF_MM;
  /** Diagonal-quadrant ring just outside each bbox corner that
   *  reveals the rotation arc and starts a rotation drag. Inner is
   *  0 so the zone starts AT the corner (easier to discover —
   *  pre-tightening, an empty 4mm inner ring buried the gesture);
   *  outer kept tight so the zone doesn't compete with normal line
   *  drags a few mm out. ConstructionTool keeps the matching values
   *  for hit-test. */
  static readonly LINE_ROTATION_CORNER_ZONE_INNER_MM = 0;
  static readonly LINE_ROTATION_CORNER_ZONE_OUTER_MM = 5;

  /** Standalone corner arc + arrowheads for a NON-selected line —
   *  used when the user is hovering near another line's bbox corner
   *  while the rotation-overlay sub-mode is active (issue #67
   *  follow-up). Returns a Paper group tagged with the same
   *  constructionId so the per-frame transforms can chase it during
   *  a drag. */
  private buildLineRotationHoverArc(line: ConstructionLine): paper.Item | null {
    const hover = this.lineRotationHoverCorner;
    if (!hover || hover.constructionId !== line.id) return null;
    const b = getUnrotatedBounds(line, this.scope);
    if (!b || b.width <= 0 || b.height <= 0) return null;
    const arc = this.constructionOverlayRenderer.buildRotationCornerArc(
      new this.scope.Rectangle(b.x, b.y, b.width, b.height),
      hover.corner,
      line.id,
    );
    if (!arc) return null;
    const rot = line.rotation ?? 0;
    if (Math.abs(rot) > 1e-6) {
      arc.rotate(rot, new this.scope.Point(b.cx, b.cy));
    }
    return arc;
  }

  /** Arm length (mm) of the chevron arrowheads on every rotation
   *  indicator — corner arcs and anchor-point hints. Hard-coded in
   *  mm so the two kinds of indicators render at the same visual
   *  size regardless of their parent arc radius. */
  static readonly LINE_ROTATION_ARROW_ARM_MM = 0.96;

  /** Half-length of the pivot crosshair drawn while a rotation drag
   *  is live. */
  static readonly ROTATION_PIVOT_CROSSHAIR_MM = 5;

  /** Small `+` marker at the rotation pivot — surfaces "you're
   *  spinning about THIS point" during a live rotation drag. Made
   *  ephemeral so paper's hit-test ignores it (the drag is already
   *  in flight; nothing should steal events). Added to the root
   *  layer so it survives across the full conGroup rebuild a scene
   *  render would otherwise wipe. */
  buildRotationPivotCrosshair(cx: number, cy: number): paper.Item {
    // Screen-constant crosshair (#864): half-length is "screen px at
    // zoom 1", divided by the live zoom so the `+` holds its on-screen
    // size, matching the paired pivot hit-test in rotationController.
    const zoom = this.scope.view?.zoom ?? 1;
    const len = Scene.ROTATION_PIVOT_CROSSHAIR_MM / zoom;
    const horiz = new this.scope.Path.Line(
      new this.scope.Point(cx - len, cy),
      new this.scope.Point(cx + len, cy),
    );
    const vert = new this.scope.Path.Line(
      new this.scope.Point(cx, cy - len),
      new this.scope.Point(cx, cy + len),
    );
    for (const seg of [horiz, vert]) {
      seg.strokeColor = new this.scope.Color(SELECTION_STROKE());
      seg.strokeWidth = 1.8;
      seg.strokeScaling = false;
      seg.fillColor = null;
    }
    const group = new this.scope.Group([horiz, vert]);
    group.data = { kind: "rotation-pivot-crosshair", ephemeral: true };
    this.rootLayer.addChild(group);
    return group;
  }

  /** Always-visible rotation pivot crosshair drawn as part of the
   *  select-mode selection overlay (issue #420). Same `+` geometry as
   *  the transient drag crosshair but NOT added to the root layer
   *  (the caller inserts it into the construction overlay group) and
   *  tagged with a distinct `kind` so the transient lifecycle can't
   *  clobber it. The owner id lets the tool's geometric pivot
   *  hit-test target it. */
  private buildPersistentPivotCrosshair(
    pivot: { x: number; y: number },
    owner:
      | { kind: "line"; id: string }
      | { kind: "group"; id: string }
      | { kind: "image"; id: string },
  ): paper.Item {
    // Screen-constant crosshair (#864) — see buildRotationPivotCrosshair.
    const zoom = this.scope.view?.zoom ?? 1;
    const len = Scene.ROTATION_PIVOT_CROSSHAIR_MM / zoom;
    const horiz = new this.scope.Path.Line(
      new this.scope.Point(pivot.x - len, pivot.y),
      new this.scope.Point(pivot.x + len, pivot.y),
    );
    const vert = new this.scope.Path.Line(
      new this.scope.Point(pivot.x, pivot.y - len),
      new this.scope.Point(pivot.x, pivot.y + len),
    );
    for (const seg of [horiz, vert]) {
      seg.strokeColor = new this.scope.Color(SELECTION_STROKE());
      seg.strokeWidth = 1.8;
      seg.strokeScaling = false;
      seg.fillColor = null;
    }
    const group = new this.scope.Group([horiz, vert]);
    group.data = {
      kind: "rotation-pivot-marker",
      ...(owner.kind === "line"
        ? { constructionId: owner.id }
        : owner.kind === "image"
          ? { tracingImageId: owner.id }
          : { constructionGroupId: owner.id }),
    };
    return group;
  }

  /** Show / move / hide the crosshair that tracks the cursor while
   *  the rotation pivot is being dragged (issue #420). Rebuilt each
   *  frame (cheap — two line segments) so it follows the snapped
   *  pivot position. While live, the always-on persistent markers are
   *  removed so only the dragging crosshair shows; the next full
   *  render redraws the persistent marker at the committed location. */
  setTransientPivot(spec: { x: number; y: number } | null): void {
    this.transientTransform.setTransientPivot(spec);
  }

  /** Translate the always-on pivot marker(s) in lock-step with a live
   *  object move (issue #420). Both the default pivot (bbox centre)
   *  and a custom `pivotPoint` move rigidly by the drag delta, so a
   *  plain translate keeps the crosshair on the shape instead of
   *  freezing until commit. */
  translatePivotMarkers(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    const items = this.rootLayer.getItems({
      match: (it: paper.Item) =>
        (it.data as { kind?: string } | undefined)?.kind ===
        "rotation-pivot-marker",
    });
    const delta = new this.scope.Point(dx, dy);
    for (const it of items) it.translate(delta);
  }
}

function isEphemeral(item: paper.Item | null): boolean {
  let i: paper.Item | null = item;
  while (i) {
    if (i.data?.ephemeral) return true;
    i = i.parent;
  }
  return false;
}

export function pathFromBuffer(buffer: { x: number; y: number }[]): string {
  if (!buffer.length) return "";
  const [first, ...rest] = buffer;
  let d = `M${first!.x.toFixed(3)} ${first!.y.toFixed(3)}`;
  for (const p of rest) d += ` L${p.x.toFixed(3)} ${p.y.toFixed(3)}`;
  return d;
}

/** Paper.js typings only allow Segment[]; runtime accepts Point[]. */
export function setPathPoints(path: paper.Path, points: paper.Point[]): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (path.addSegments as unknown as (s: paper.Point[]) => void)(points);
}
